import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ControlprojectService,
  ControlProject,
  ControlProjectMember,
  ControlProjectTapVersion,
  ControlProjectMilestone,
  ControlProjectAttachment
} from '../../services/controlproject.service';
import { ControltaskService, ControlTask } from '../../services/controltask.service';
import { ControlhelpService } from '../../services/controlhelp.service';
import { UserSessionService } from '../../services/user-session.service';
import { UserService } from '../../services/user.service';
import { OrgStructureService } from '../../services/org-structure.service';
import { API_BASE_URL } from '../../services/api-base';
import { toLocalDateTimeInputValue, toLocalISODate, toLocalISOString, toLocalSQLDateTime } from '../../utils/date-utils';
import { forkJoin, of } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

type ProjectStatus = 'DRAFT' | 'PLANNED' | 'IN_PROGRESS' | 'PAUSED' | 'COMPLETED' | 'CANCELED';
type ProjectStatusFilter = ProjectStatus | 'ALL';
type ProjectTaskView = 'PENDING' | 'COMPLETED' | 'ALL';
type ProjectTaskSort = 'DUE_ASC' | 'DUE_DESC' | 'UPDATED_DESC' | 'RESPONSIBLE_ASC' | 'PRIORITY_DESC';

type ControlHelpStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'WAITING_USER'
  | 'WAITING_THIRD_PARTY'
  | 'RESOLVED'
  | 'CLOSED'
  | 'CANCELED';

type ControlHelpPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

type RelatedModule = 'CONTROLTASK' | 'CONTROLDOCS' | 'CONTROLSIGN' | 'CONTROLCLI' | 'OTHER';

type HistoryItemType = 'PROJECT' | 'MILESTONE' | 'TASK' | 'TICKET' | 'TAP';

interface ControlHelpTicket {
  id: number;
  ticket_code: string;
  title: string;
  description: string;
  status: ControlHelpStatus;
  priority: ControlHelpPriority;
  category?: string;
  subcategory?: string;
  department_id?: number | null;
  department_name?: string | null;
  created_by?: number | null;
  created_by_name?: string | null;
  assigned_to?: number | null;
  assigned_to_name?: string | null;
  related_module: RelatedModule;
  opened_at: string;
  last_action_at?: string | null;
  response_at?: string | null;
  closed_at?: string | null;
  due_at?: string | null;
}

interface ControlHelpComment {
  id: number;
  user_id?: number;
  user_name?: string;
  comment: string;
  is_internal: boolean;
  created_at: string;
}

interface ControlHelpAttachment {
  id: number;
  comment_id?: number | null;
  file_name: string;
  file_path?: string;
  file_type?: string;
  file_size?: number;
  uploaded_by?: number;
  uploaded_by_name?: string;
  uploaded_at: string;
}

interface ControlHelpHistory {
  id: number;
  action_type: string;
  old_value?: string | null;
  new_value?: string | null;
  description?: string | null;
  user_id?: number | null;
  user_name?: string | null;
  created_at: string;
}

interface ControlHelpCategory {
  id: number;
  department_id?: number | null;
  name: string;
  subcategory?: string | null;
  description?: string;
  active: boolean;
}

interface ProjectHistoryItem {
  type: HistoryItemType;
  title: string;
  date?: string | null;
  userName?: string | null;
  details?: string | null;
  description?: string | null;
}

interface ProjectAttachmentItem {
  id: number;
  tenant_id: number;
  project_id: number;
  file_name: string;
  file_path: string;
  file_type?: string | null;
  file_size?: number;
  uploaded_by: number;
  uploaded_at: string;
  file_url: string;
  is_image?: boolean;
}

@Component({
  selector: 'app-controlproject',
  templateUrl: './controlproject.component.html',
  styleUrls: ['./controlproject.component.scss']
})
export class ControlprojectComponent implements OnInit {
  private readonly activeProjectStorageKey = 'controlproject.activeProjectId';
  private readonly statusFiltersStorageKey = 'controlproject.statusFilters';
  tenantId: number | null = null;
  userId: number | null = null;
  userRoleName = '';
  userDepartmentId: number | null = null;
  userSectorId: number | null = null;

  projects: ControlProject[] = [];
  activeProject: ControlProject | null = null;
  projectMembers: ControlProjectMember[] = [];
  tapVersions: ControlProjectTapVersion[] = [];
  milestones: ControlProjectMilestone[] = [];
  tasks: ControlTask[] = [];
  visibleProjectTasks: ControlTask[] = [];
  tickets: any[] = [];
  history: any[] = [];
  historyTimeline: ProjectHistoryItem[] = [];
  historySidebarSearchTerm = '';
  historySidebarPageSize = 5;
  historySidebarCurrentPage = 1;
  tapExpandedFields: Record<string, boolean> = {};
  projectTaskView: ProjectTaskView = 'PENDING';
  projectTaskSort: ProjectTaskSort = 'DUE_ASC';

  users: Array<{
    id: number;
    name: string;
    department_id?: number | null;
    department_name?: string | null;
    sector_name?: string | null;
    picture_user?: string | null;
    picture_url?: string | null;
    avatar?: string | null;
    photo?: string | null;
  }> = [];
  departments: Array<{ id: number; name: string; sector_id?: number | null; sector_name?: string }> = [];

  showNewProjectModal = false;
  showTapModal = false;
  showMemberModal = false;
  showMilestoneModal = false;
  showMilestoneDeleteModal = false;
  showDateEditModal = false;
  showTaskModal = false;
  showTaskDetailModal = false;
  showNewTicketModal = false;
  showTicketDetail = false;
  memberSearch = '';
  taskAssigneeSearch = '';
  taskDetailAssigneeSearch = '';
  projectAssigneeOptions: Array<{ id: number; name: string }> = [];
  filteredTaskAssigneeOptions: Array<{ id: number; name: string }> = [];
  filteredTaskDetailAssigneeOptions: Array<{ id: number; name: string }> = [];
  @ViewChild('memberSearchInput') memberSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('taskAssigneeSearchInput') taskAssigneeSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('taskDetailAssigneeSearchInput') taskDetailAssigneeSearchInput?: ElementRef<HTMLInputElement>;
  savingNewTicket = false;
  deletingTicketId: number | null = null;
  ticketDetailTab: 'details' | 'comments' | 'history' | 'attachments' = 'details';
  editingTapId: number | null = null;
  editingDraftId: number | null = null;
  draftOriginalDates = { start_date: '', end_date: '' };
  draftDateReason = '';
  inlineBucketLoading = false;
  editingProjectDescription = false;
  projectDescriptionDraft = '';
  projectDescriptionExpanded = false;
  savingProjectDescription = false;
  milestoneToDelete: ControlProjectMilestone | null = null;

  statusFilters: ProjectStatusFilter[] = ['IN_PROGRESS'];
  statusFiltersPinned = false;
  ownershipFilter: 'ALL' | 'MINE' = 'ALL';
  sortOption: 'CREATED_DESC' | 'NAME_ASC' | 'UPDATED_DESC' | 'STATUS_ASC' = 'CREATED_DESC';
  searchTerm = '';
  projectPageSize = 10;
  projectCurrentPage = 1;
  activeMainTab: 'dashboard' | 'projects' = 'dashboard';
  projectDashboardPeriodMonths = 3;
  projectDashboardKpis = {
    projectsPeriod: 0,
    completedPeriod: 0,
    completionRate: 0,
    overdueOpen: 0
  };
  projectMonthlySeries: Array<{ label: string; created: number; completed: number }> = [];
  projectStatusRows: Array<{ label: string; count: number; percent: number; color: string }> = [];
  projectManagerRows: Array<{ label: string; count: number; percent: number }> = [];
  projectDepartmentRows: Array<{ label: string; count: number; percent: number; color: string }> = [];
  projectInsights = {
    totalProjects: 0,
    activeProjects: 0,
    avgCreatedPerMonth: 0,
    avgCompletedPerMonth: 0
  };
  projectLineTooltip = {
    visible: false,
    x: 0,
    y: 0,
    title: '',
    value: ''
  };

  detailTab: 'calendar' | 'tasks' | 'tickets' | 'tap' | 'team' | 'attachments' = 'calendar';
  projectAttachments: ProjectAttachmentItem[] = [];

  calendarMonth = new Date();
  calendarDays: Array<{ date: Date; label: number; isCurrentMonth: boolean }> = [];

  newProjectForm = {
    name: '',
    description: '',
    status: 'PLANNED' as ProjectStatus | 'DRAFT',
    start_date: '',
    end_date: '',
    manager_user_id: '',
    department_id: ''
  };
  projectStartDateDisplay = '';
  projectEndDateDisplay = '';
  projectStartDatePickerValue: Date | null = null;
  projectEndDatePickerValue: Date | null = null;
  projectStartDateInvalid = false;
  projectEndDateInvalid = false;
  milestoneDueDateDisplay = '';
  milestoneDueDatePickerValue: Date | null = null;
  milestoneDueDateInvalid = false;
  dateEditStartDateDisplay = '';
  dateEditEndDateDisplay = '';
  dateEditStartDatePickerValue: Date | null = null;
  dateEditEndDatePickerValue: Date | null = null;
  dateEditStartDateInvalid = false;
  dateEditEndDateInvalid = false;
  taskStartDateDisplay = '';
  taskDueDateDisplay = '';
  taskStartDatePickerValue: Date | null = null;
  taskDueDatePickerValue: Date | null = null;
  taskStartDateInvalid = false;
  taskDueDateInvalid = false;
  taskDetailStartDateDisplay = '';
  taskDetailDueDateDisplay = '';
  taskDetailStartDatePickerValue: Date | null = null;
  taskDetailDueDatePickerValue: Date | null = null;
  taskDetailStartDateInvalid = false;
  taskDetailDueDateInvalid = false;

  tapForm = {
    title: '',
    justification: '',
    objectives: '',
    business_scope: '',
    technical_scope: '',
    deliverables: '',
    non_deliverables: '',
    permissions: '',
    constraints: '',
    assumptions: '',
    risks: '',
    success_criteria: '',
    budget: ''
  };

  newMemberForm = {
    user_id: '',
    role: 'MEMBER' as 'MANAGER' | 'MEMBER' | 'OBSERVER'
  };

  newMilestoneForm = {
    title: '',
    description: '',
    due_date: '',
    status: 'PLANNED' as 'PLANNED' | 'DONE' | 'LATE'
  };

  dateEditForm = {
    start_date: '',
    end_date: '',
    reason: ''
  };

  activeTicket: ControlHelpTicket | null = null;
  ticketComments: ControlHelpComment[] = [];
  ticketAttachments: ControlHelpAttachment[] = [];
  ticketHistory: ControlHelpHistory[] = [];
  ticketWatchers: string[] = [];
  ticketCategories: ControlHelpCategory[] = [];

  newTicketForm = {
    title: '',
    description: '',
    priority: 'MEDIUM' as ControlHelpPriority,
    category: '',
    subcategory: '',
    department_id: '',
    origin_department_id: ''
  };
  newTicketRequestUuid = '';
  newTicketFiles: File[] = [];
  draggingNewTicket = false;
  draggingTicketDetailAttachment = false;
  newTaskFiles: File[] = [];
  taskModalDragging = false;
  private readonly taskFilePreviewUrls = new WeakMap<File, string>();

  newCommentForm = {
    comment: '',
    is_internal: false
  };
  commentFile: File | null = null;

  groups: any[] = [];
  subgroups: any[] = [];
  buckets: any[] = [];
  detailFormSubgroups: any[] = [];
  detailFormBuckets: any[] = [];
  tags: any[] = [];
  members: any[] = [];

  taskForm: FormGroup;
  taskDetailForm: FormGroup;
  draftSubtasks: string[] = [];
  activeTask: ControlTask | null = null;
  detailSubtaskText = '';
  detailCommentText = '';
  detailCommentFile: File | null = null;
  editingProjectTitle = false;
  savingProjectTitle = false;
  projectTitleDraft = '';
  editingSubtaskId: number | null = null;
  editingSubtaskText = '';
  editingCommentId: number | null = null;
  editingCommentText = '';
  private commentFileInputElement?: HTMLInputElement | null;
  private readonly fileHostUrl = `${API_BASE_URL}`;

  constructor(
    private controlprojectService: ControlprojectService,
    private controltaskService: ControltaskService,
    private controlhelpService: ControlhelpService,
    private userSession: UserSessionService,
    private userService: UserService,
    private orgStructureService: OrgStructureService,
    private fb: FormBuilder
  ) {
    this.taskForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      priority: ['MEDIUM'],
      visibility: ['PRIVATE'],
      status: ['OPEN'],
      start_date: [''],
      due_date: [''],
      subgroupId: [''],
      bucketId: [''],
      groupId: [''],
      tagIds: [[]],
      assigneeId: [''],
      newSubtask: [''],
      inlineBucketName: [''],
      inlineBucketDescription: ['']
    });

    this.taskDetailForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      status: ['OPEN'],
      priority: ['MEDIUM'],
      visibility: ['PRIVATE'],
      start_date: [''],
      due_date: [''],
      groupId: [''],
      subgroupId: [''],
      bucketId: [''],
      tagIds: [[]],
      assigneeId: ['']
    });
  }

  ngOnInit() {
    const user = this.userSession.getUser();
    this.tenantId = user?.tenant_id || null;
    this.userId = user?.id || null;
    this.userRoleName = String(user?.role_name || user?.role?.name || '').toLowerCase();
    this.userDepartmentId = user?.['department_id'] ?? null;
    this.userSectorId = user?.['sector_id'] ?? user?.['sector']?.id ?? null;
    this.clearPersistedActiveProjectId();
    this.restorePersistedStatusFilters();
    this.loadProjects();
    this.fetchUsers();
    this.fetchDepartments();
    this.loadGroups();
    this.loadTags();
    this.loadSubgroups();
    this.loadBuckets();
    this.loadMembers();
    this.fetchTicketCategories();
    this.buildCalendar();
  }

  setActiveMainTab(tab: 'dashboard' | 'projects') {
    this.activeMainTab = tab;
    if (tab === 'dashboard') {
      this.buildProjectDashboardInsights();
    }
  }

  setProjectDashboardPeriod(months: number) {
    if (this.projectDashboardPeriodMonths === months) {
      return;
    }
    this.projectDashboardPeriodMonths = months;
    this.buildProjectDashboardInsights();
  }

  onProjectFiltersChange() {
    if (this.statusFilters.length === 0 || this.statusFilters.includes('ALL')) {
      this.statusFilters = ['ALL'];
    }
    this.persistStatusFiltersPreference();
    this.projectCurrentPage = 1;
  }

  toggleStatusFilterPin() {
    this.statusFiltersPinned = !this.statusFiltersPinned;
    if (this.statusFiltersPinned) {
      this.persistStatusFiltersPreference();
      return;
    }
    this.clearPersistedStatusFilters();
  }

  get filteredProjects() {
    const term = (this.searchTerm || '').toLowerCase();
    const filtered = this.projects.filter((project) => {
      if (this.ownershipFilter === 'MINE' && this.userId && project.created_by !== this.userId) {
        return false;
      }
      if (this.statusFilters.length && !this.statusFilters.includes('ALL') && !this.statusFilters.includes(project.status as ProjectStatus)) {
        return false;
      }
      if (!term) {
        return true;
      }
      return (
        project.name.toLowerCase().includes(term) ||
        (project.code || '').toLowerCase().includes(term)
      );
    });
    return filtered.sort((a, b) => this.sortProjects(a, b));
  }

  get paginatedFilteredProjects() {
    const start = (this.projectCurrentPage - 1) * this.projectPageSize;
    return this.filteredProjects.slice(start, start + this.projectPageSize);
  }

  get projectTotalPages() {
    return Math.max(1, Math.ceil(this.filteredProjects.length / this.projectPageSize));
  }

  get projectPageStart() {
    if (!this.filteredProjects.length) {
      return 0;
    }
    return (this.projectCurrentPage - 1) * this.projectPageSize + 1;
  }

  get projectPageEnd() {
    return Math.min(this.projectCurrentPage * this.projectPageSize, this.filteredProjects.length);
  }

  prevProjectPage() {
    if (this.projectCurrentPage <= 1) {
      return;
    }
    this.projectCurrentPage -= 1;
  }

  nextProjectPage() {
    if (this.projectCurrentPage >= this.projectTotalPages) {
      return;
    }
    this.projectCurrentPage += 1;
  }

  private sortProjects(a: ControlProject, b: ControlProject) {
    switch (this.sortOption) {
      case 'NAME_ASC':
        return (a.name || '').localeCompare(b.name || '', 'pt-BR');
      case 'UPDATED_DESC': {
        const aDate = this.sortDateValue(a.updated_at || a.created_at);
        const bDate = this.sortDateValue(b.updated_at || b.created_at);
        return bDate - aDate;
      }
      case 'STATUS_ASC':
        return (a.status || '').localeCompare(b.status || '', 'pt-BR');
      case 'CREATED_DESC':
      default: {
        const aDate = this.sortDateValue(a.created_at);
        const bDate = this.sortDateValue(b.created_at);
        return bDate - aDate;
      }
    }
  }

  private sortDateValue(value?: string | null) {
    if (!value) {
      return 0;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 0;
    }
    return parsed.getTime();
  }

  projectsByStatus(status: ProjectStatus) {
    return this.projects.filter((project) => project.status === status).length;
  }

  private buildProjectDashboardInsights() {
    const months = this.projectDashboardPeriodMonths;
    const now = new Date();
    const endMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startMonth = new Date(endMonth.getFullYear(), endMonth.getMonth() - (months - 1), 1);
    const endDate = new Date(endMonth.getFullYear(), endMonth.getMonth() + 1, 0, 23, 59, 59, 999);

    const periodMap = new Map<string, { label: string; created: number; completed: number }>();
    for (let offset = 0; offset < months; offset += 1) {
      const monthDate = new Date(startMonth.getFullYear(), startMonth.getMonth() + offset, 1);
      const key = this.projectMonthKey(monthDate);
      periodMap.set(key, {
        label: this.projectMonthLabel(monthDate),
        created: 0,
        completed: 0
      });
    }

    const projectsInPeriod = this.projects.filter((project) => {
      const createdDate = this.parseProjectDate(project.created_at);
      if (!createdDate) {
        return false;
      }
      return createdDate >= startMonth && createdDate <= endDate;
    });

    projectsInPeriod.forEach((project) => {
      const createdDate = this.parseProjectDate(project.created_at);
      if (createdDate) {
        const createdKey = this.projectMonthKey(createdDate);
        const createdRow = periodMap.get(createdKey);
        if (createdRow) {
          createdRow.created += 1;
        }
      }

      if (project.status === 'COMPLETED') {
        const completionDate =
          this.parseProjectDate(project.end_date) ||
          this.parseProjectDate(project.updated_at) ||
          this.parseProjectDate(project.created_at);
        if (completionDate && completionDate >= startMonth && completionDate <= endDate) {
          const completedKey = this.projectMonthKey(completionDate);
          const completedRow = periodMap.get(completedKey);
          if (completedRow) {
            completedRow.completed += 1;
          }
        }
      }
    });

    const periodSeries = Array.from(periodMap.values());
    const totalCreated = periodSeries.reduce((sum, row) => sum + row.created, 0);
    const totalCompleted = periodSeries.reduce((sum, row) => sum + row.completed, 0);

    const overdueOpen = this.projects.filter((project) => {
      if (!project.end_date) {
        return false;
      }
      if (project.status === 'COMPLETED' || project.status === 'CANCELED') {
        return false;
      }
      const end = this.parseProjectDate(project.end_date);
      return !!(end && end.getTime() < now.getTime());
    }).length;

    const statusLabelMap: Record<string, string> = {
      DRAFT: 'Rascunho',
      PLANNED: 'Planejado',
      IN_PROGRESS: 'Em andamento',
      PAUSED: 'Pausado',
      COMPLETED: 'Concluído',
      CANCELED: 'Cancelado'
    };
    const statusColorMap: Record<string, string> = {
      DRAFT: '#94a3b8',
      PLANNED: '#4fb7e8',
      IN_PROGRESS: '#22b8b3',
      PAUSED: '#f0cb59',
      COMPLETED: '#4ac17d',
      CANCELED: '#f57f8d'
    };
    const statusCounts = new Map<string, number>();
    this.projects.forEach((project) => {
      const statusKey = String(project.status || 'DRAFT');
      statusCounts.set(statusKey, (statusCounts.get(statusKey) || 0) + 1);
    });
    const totalProjects = this.projects.length;
    this.projectStatusRows = Array.from(statusCounts.entries())
      .map(([status, count]) => ({
        label: statusLabelMap[status] || status,
        count,
        percent: totalProjects ? Math.round((count / totalProjects) * 100) : 0,
        color: statusColorMap[status] || '#22b8b3'
      }))
      .sort((a, b) => b.count - a.count);

    const managerMap = new Map<string, number>();
    this.projects.forEach((project) => {
      const manager = String(project.manager_name || 'Sem gerente').trim() || 'Sem gerente';
      managerMap.set(manager, (managerMap.get(manager) || 0) + 1);
    });
    this.projectManagerRows = Array.from(managerMap.entries())
      .map(([label, count]) => ({
        label,
        count,
        percent: totalProjects ? Math.round((count / totalProjects) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);

    const departmentPalette = [
      '#22b8b3',
      '#4fb7e8',
      '#74d6d2',
      '#9adce9',
      '#b9e5ee',
      '#d7eff5'
    ];
    const departmentMap = new Map<string, number>();
    this.projects.forEach((project) => {
      const department = String(project.department_name || 'Sem departamento').trim() || 'Sem departamento';
      departmentMap.set(department, (departmentMap.get(department) || 0) + 1);
    });
    this.projectDepartmentRows = Array.from(departmentMap.entries())
      .map(([label, count], index) => ({
        label,
        count,
        percent: totalProjects ? Math.round((count / totalProjects) * 100) : 0,
        color: departmentPalette[index % departmentPalette.length]
      }))
      .sort((a, b) => b.count - a.count);

    this.projectDashboardKpis = {
      projectsPeriod: totalCreated,
      completedPeriod: totalCompleted,
      completionRate: totalCreated ? (totalCompleted / totalCreated) * 100 : 0,
      overdueOpen
    };

    this.projectMonthlySeries = periodSeries;
    this.projectInsights = {
      totalProjects,
      activeProjects: this.projects.filter((project) => !['COMPLETED', 'CANCELED'].includes(project.status)).length,
      avgCreatedPerMonth: months ? totalCreated / months : 0,
      avgCompletedPerMonth: months ? totalCompleted / months : 0
    };
  }

  private parseProjectDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private projectMonthKey(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  private projectMonthLabel(date: Date) {
    const month = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    const monthLabel = month.charAt(0).toUpperCase() + month.slice(1);
    const yearLabel = String(date.getFullYear()).slice(-2);
    return `${monthLabel} ${yearLabel}`;
  }

  get projectCreatedSeriesValues() {
    return this.projectMonthlySeries.map((row) => Number(row.created || 0));
  }

  get projectCompletedSeriesValues() {
    return this.projectMonthlySeries.map((row) => Number(row.completed || 0));
  }

  get projectCreatedAveragePerMonth() {
    const values = this.projectCreatedSeriesValues;
    if (!values.length) {
      return 0;
    }
    const total = values.reduce((sum, value) => sum + value, 0);
    return Number((total / values.length).toFixed(2));
  }

  get projectCreatedAveragePerMonthLabel() {
    return this.projectCreatedAveragePerMonth.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  }

  get projectLineMaxValue() {
    return Math.max(...this.projectCreatedSeriesValues, ...this.projectCompletedSeriesValues, 1);
  }

  projectLinePointList(values: number[]) {
    return this.projectLineDots(values)
      .map((point) => `${point.x},${point.y}`)
      .join(' ');
  }

  projectLineYByValue(value: number) {
    const chartTop = 18;
    const chartBottom = 192;
    const maxValue = this.projectLineMaxValue || 1;
    const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
    return Number(y.toFixed(2));
  }

  get projectLineYAxisTicks() {
    const max = Math.max(1, Math.ceil(this.projectLineMaxValue));
    const divisions = 4;
    const step = max / divisions;
    const ticks: Array<{ label: string; value: number; y: number }> = [];
    for (let i = 0; i <= divisions; i += 1) {
      const value = Number((max - i * step).toFixed(1));
      const pretty = Number.isInteger(value)
        ? `${value}`
        : value.toLocaleString('pt-BR', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        });
      ticks.push({
        label: pretty,
        value,
        y: this.projectLineYByValue(value)
      });
    }
    return ticks;
  }

  projectLineDots(values: number[]) {
    const points: Array<{ x: number; y: number; value: number; label: string }> = [];
    if (!values.length) {
      return points;
    }
    const widthStart = 36;
    const widthEnd = 616;
    const chartTop = 18;
    const chartBottom = 192;
    const maxValue = this.projectLineMaxValue;
    const step = values.length > 1 ? (widthEnd - widthStart) / (values.length - 1) : 0;
    values.forEach((value, index) => {
      const x = widthStart + step * index;
      const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
      points.push({
        x: Number(x.toFixed(2)),
        y: Number(y.toFixed(2)),
        value: Number(value || 0),
        label: this.projectMonthlySeries[index]?.label || ''
      });
    });
    return points;
  }

  projectLinePointEnter(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'created' | 'completed'
  ) {
    this.updateProjectLineTooltip(event, point, series);
  }

  projectLinePointMove(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'created' | 'completed'
  ) {
    this.updateProjectLineTooltip(event, point, series);
  }

  projectLinePointLeave() {
    this.projectLineTooltip.visible = false;
  }

  private updateProjectLineTooltip(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'created' | 'completed'
  ) {
    const target = event.currentTarget as SVGCircleElement | null;
    const container = target?.closest('.project-v2-line-chart') as HTMLElement | null;
    if (!container) {
      this.projectLineTooltip.visible = false;
      return;
    }
    const rect = container.getBoundingClientRect();
    let x = event.clientX - rect.left + 14;
    let y = event.clientY - rect.top - 12;
    x = Math.max(90, Math.min(x, rect.width - 20));
    y = Math.max(26, Math.min(y, rect.height - 20));
    const isCreated = series === 'created';
    this.projectLineTooltip = {
      visible: true,
      x,
      y,
      title: `${isCreated ? 'Criados' : 'Concluídos'} · ${point.label}`,
      value: `${point.value} projeto(s)`
    };
  }

  projectNumber(value: number) {
    return Number.isFinite(value) ? value.toLocaleString('pt-BR') : '0';
  }

  projectDecimal(value: number) {
    return Number.isFinite(value)
      ? value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      : '0,0';
  }

  projectPercent(value: number) {
    return Number.isFinite(value)
      ? `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
      : '0,0%';
  }

  get projectStatusDonutStyle() {
    return this.buildDonutStyle(this.projectStatusRows);
  }

  get projectDepartmentDonutStyle() {
    return this.buildDonutStyle(this.projectDepartmentRows);
  }

  private buildDonutStyle(rows: Array<{ percent: number; color: string }>) {
    if (!rows.length) {
      return 'conic-gradient(#e5edf7 0 100%)';
    }
    let cursor = 0;
    const parts = rows.map((row) => {
      const slice = row.percent > 0 ? row.percent : 0;
      const start = cursor;
      cursor += slice;
      return `${row.color} ${start}% ${cursor}%`;
    });
    if (cursor < 100) {
      parts.push(`#e5edf7 ${cursor}% 100%`);
    }
    return `conic-gradient(${parts.join(', ')})`;
  }

  get totalTasksCount() {
    return this.tasks.length;
  }

  get completedTasksCount() {
    return this.tasks.filter((task) => task.status === 'COMPLETED').length;
  }

  refreshVisibleProjectTasks() {
    const filtered = this.tasks.filter((task) => {
      const completed = String(task.status || '').toUpperCase() === 'COMPLETED';
      if (this.projectTaskView === 'COMPLETED') {
        return completed;
      }
      if (this.projectTaskView === 'PENDING') {
        return !completed;
      }
      return true;
    });

    this.visibleProjectTasks = filtered.slice().sort((left, right) => this.compareProjectTasks(left, right));
  }

  trackByTaskId(_index: number, task: ControlTask) {
    return task.id;
  }

  get projectCompletionPercent() {
    if (!this.totalTasksCount) {
      return 0;
    }
    return Math.round((this.completedTasksCount / this.totalTasksCount) * 100);
  }

  get projectManagerDonutStyle() {
    const progress = Math.max(0, Math.min(100, this.projectCompletionPercent || 0));
    return `conic-gradient(#27c2bc 0 ${progress}%, #d9e4f3 ${progress}% 100%)`;
  }

  get projectLastUpdateLabel() {
    const latest = this.historyTimeline[0]?.date || null;
    if (latest) {
      return this.formatDateTimeLabel(latest);
    }
    const fallback = this.activeProject?.updated_at || this.activeProject?.created_at;
    return fallback ? this.formatDateTimeLabel(fallback) : '—';
  }

  get filteredHistoryTimeline() {
    const term = (this.historySidebarSearchTerm || '').trim().toLowerCase();
    if (!term) {
      return this.historyTimeline;
    }

    return this.historyTimeline.filter((item) => {
      const fields = [
        item.title,
        item.userName,
        item.details,
        item.description,
        this.historyTypeLabel(item.type),
        item.date ? new Date(item.date).toLocaleDateString('pt-BR') : ''
      ];
      return fields.some((value) => String(value || '').toLowerCase().includes(term));
    });
  }

  get historySidebarTotalPages() {
    return Math.max(1, Math.ceil(this.filteredHistoryTimeline.length / this.historySidebarPageSize));
  }

  get paginatedHistoryTimeline() {
    const start = (this.historySidebarCurrentPage - 1) * this.historySidebarPageSize;
    return this.filteredHistoryTimeline.slice(start, start + this.historySidebarPageSize);
  }

  get historySidebarPageStart() {
    if (!this.filteredHistoryTimeline.length) {
      return 0;
    }
    return (this.historySidebarCurrentPage - 1) * this.historySidebarPageSize + 1;
  }

  get historySidebarPageEnd() {
    return Math.min(this.historySidebarCurrentPage * this.historySidebarPageSize, this.filteredHistoryTimeline.length);
  }

  onHistorySidebarSearchChange() {
    this.historySidebarCurrentPage = 1;
    this.syncHistorySidebarPagination();
  }

  prevHistorySidebarPage() {
    if (this.historySidebarCurrentPage <= 1) {
      return;
    }
    this.historySidebarCurrentPage -= 1;
  }

  nextHistorySidebarPage() {
    if (this.historySidebarCurrentPage >= this.historySidebarTotalPages) {
      return;
    }
    this.historySidebarCurrentPage += 1;
  }

  projectLastUpdateLabelFor(project: ControlProject) {
    const fallback = project.updated_at || project.created_at;
    return fallback ? this.formatDateTimeLabel(fallback) : '—';
  }

  taskLastUpdateLabel(task: ControlTask) {
    const dateValue = task.updated_at || task.created_at || task.due_date || task.start_date;
    if (!dateValue) {
      return '—';
    }
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  taskAssigneeLabel(task: ControlTask) {
    const assigneeNames = this.taskResponsibleMembers(task)
      .map((member: any) => String(member?.name || '').trim())
      .filter(Boolean);
    return assigneeNames.length ? assigneeNames.join(', ') : 'Não atribuído';
  }

  private taskResponsibleMembers(task: ControlTask) {
    const members = task.members || [];
    const assignees = members.filter(
      (member: any) => String(member?.role || '').toUpperCase() === 'ASSIGNEE'
    );
    if (assignees.length) {
      return assignees;
    }
    return members.filter((member: any) => String(member?.role || '').toUpperCase() === 'OWNER');
  }

  private taskResponsibleId(task: ControlTask) {
    return this.taskResponsibleMembers(task)[0]?.id || '';
  }

  private compareProjectTasks(left: ControlTask, right: ControlTask) {
    if (this.projectTaskSort === 'RESPONSIBLE_ASC') {
      return this.taskAssigneeLabel(left).localeCompare(this.taskAssigneeLabel(right), 'pt-BR');
    }
    if (this.projectTaskSort === 'UPDATED_DESC') {
      return this.taskSortTimestamp(right.updated_at || right.created_at)
        - this.taskSortTimestamp(left.updated_at || left.created_at);
    }
    if (this.projectTaskSort === 'PRIORITY_DESC') {
      const weights: Record<string, number> = { URGENT: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
      return (weights[String(right.priority || '').toUpperCase()] || 0)
        - (weights[String(left.priority || '').toUpperCase()] || 0);
    }

    const leftDue = this.taskDueSortTimestamp(left);
    const rightDue = this.taskDueSortTimestamp(right);
    if (leftDue === null && rightDue === null) {
      return 0;
    }
    if (leftDue === null) {
      return 1;
    }
    if (rightDue === null) {
      return -1;
    }
    return this.projectTaskSort === 'DUE_DESC' ? rightDue - leftDue : leftDue - rightDue;
  }

  private taskDueSortTimestamp(task: ControlTask) {
    const dueDate = this.parseIsoDate(task.due_date);
    return dueDate ? dueDate.getTime() : null;
  }

  private taskSortTimestamp(value?: string | null) {
    if (!value) {
      return 0;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }

  taskDeadlineLabel(task: ControlTask) {
    if (!task.due_date) {
      return 'Sem prazo';
    }
    return this.isTaskOverdue(task) ? 'Fora do prazo' : 'Dentro do prazo';
  }

  taskDeadlineClass(task: ControlTask) {
    if (!task.due_date) {
      return 'no-deadline';
    }
    return this.isTaskOverdue(task) ? 'overdue' : 'on-time';
  }

  private isTaskOverdue(task: ControlTask) {
    const dueDate = this.parseIsoDate(task.due_date);
    if (!dueDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate.getTime() < today.getTime();
  }

  private formatDateTimeLabel(value: string) {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  statusLabel(status: string) {
    const map: Record<string, string> = {
      ALL: 'Todos',
      DRAFT: 'Rascunho',
      PLANNED: 'Planejado',
      IN_PROGRESS: 'Em andamento',
      PAUSED: 'Pausado',
      COMPLETED: 'Concluído',
      CANCELED: 'Cancelado'
    };
    return map[status] || status;
  }

  taskStatusLabel(status?: string | null) {
    const map: Record<string, string> = {
      OPEN: 'Aberta',
      IN_PROGRESS: 'Em andamento',
      BLOCKED: 'Bloqueada',
      COMPLETED: 'Concluída',
      AWAITING_CLIENT_RESPONSE: 'Ag. retorno cliente',
      AWAITING_BUDGET_APPROVAL: 'Ag. aprov. orçamento',
      AWAITING_SUPPLIER_RESPONSE: 'Ag. retorno fornecedor'
    };
    if (!status) {
      return 'Sem status';
    }
    return map[status] || status;
  }

  taskPriorityLabel(priority?: string | null) {
    const map: Record<string, string> = {
      LOW: 'Baixa',
      MEDIUM: 'Média',
      HIGH: 'Alta',
      URGENT: 'Urgente'
    };
    if (!priority) {
      return 'Sem prioridade';
    }
    return map[priority] || priority;
  }

  projectPersonInitials(name?: string | null) {
    const source = String(name || 'Sistema').trim();
    if (!source) {
      return 'SI';
    }
    const parts = source.split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return 'SI';
    }
    const first = parts[0].charAt(0) || '';
    const second = parts.length > 1
      ? parts[parts.length - 1].charAt(0)
      : (parts[0].charAt(1) || '');
    return `${first}${second}`.toUpperCase();
  }

  memberDisplayName(member: ControlProjectMember): string {
    const fallback = String(member.user_name || '').trim();
    if (fallback) {
      return fallback;
    }
    const user = this.users.find((u) => Number(u.id) === Number(member.user_id));
    return String(user?.name || 'Usuário');
  }

  memberRoleLabel(role?: ControlProjectMember['role'] | null): string {
    const map: Record<string, string> = {
      MANAGER: 'Gerente',
      MEMBER: 'Membro',
      OBSERVER: 'Observador'
    };
    if (!role) {
      return 'Membro';
    }
    return map[role] || role;
  }

  memberDepartmentLabel(member: ControlProjectMember): string {
    const user = this.users.find((u) => Number(u.id) === Number(member.user_id));
    if (!user) {
      return 'Departamento não informado';
    }
    if (user.department_name && user.sector_name) {
      return `${user.sector_name} / ${user.department_name}`;
    }
    if (user.department_name) {
      return user.department_name;
    }
    if (user.department_id) {
      return this.departmentLabel(user.department_id);
    }
    return 'Departamento não informado';
  }

  memberAvatarUrl(member: ControlProjectMember): string | null {
    const user = this.users.find((u) => Number(u.id) === Number(member.user_id)) as any;
    if (!user) {
      return null;
    }
    const raw = user.picture_user || user.picture_url || user.avatar || user.photo || null;
    if (!raw) {
      return null;
    }
    const source = String(raw).trim();
    if (!source) {
      return null;
    }
    if (source.startsWith('http://') || source.startsWith('https://')) {
      return source;
    }
    const normalized = source.startsWith('/') ? source : `/${source}`;
    return `${API_BASE_URL}${normalized}`;
  }

  onProjectAttachmentFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];
    if (!files.length || !this.activeProject || !this.tenantId || !this.userId) {
      if (input) {
        input.value = '';
      }
      return;
    }
    const projectId = this.activeProject.id;
    const requests = files.map((file) => {
      const formData = new FormData();
      formData.append('tenant_id', String(this.tenantId));
      formData.append('uploaded_by', String(this.userId));
      formData.append('file', file, file.name);
      return this.controlprojectService.uploadAttachment(projectId, formData);
    });
    forkJoin(requests).subscribe({
      next: (rows) => {
        const created = (rows || []).map((row) => this.normalizeProjectAttachment(row));
        this.projectAttachments = [...created, ...this.projectAttachments].sort(
          (a, b) => this.historySortValue(b.uploaded_at) - this.historySortValue(a.uploaded_at)
        );
      },
      error: () => {
        this.loadProjectAttachments(projectId);
      },
      complete: () => {
        if (input) {
          input.value = '';
        }
      }
    });
  }

  removeProjectAttachment(attachmentId: number): void {
    if (!this.activeProject || !this.tenantId) {
      return;
    }
    this.controlprojectService.deleteAttachment(this.tenantId, this.activeProject.id, attachmentId).subscribe({
      next: () => {
        this.projectAttachments = this.projectAttachments.filter((item) => item.id !== attachmentId);
      }
    });
  }

  formatFileSize(size?: number | null): string {
    const bytes = Number(size || 0);
    if (!bytes) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, exp);
    return `${value >= 100 || exp === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[exp]}`;
  }

  attachmentFileUrl(item: ProjectAttachmentItem): string {
    const raw = String(item?.file_url || '').trim();
    if (!raw) {
      return '';
    }
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      return raw;
    }
    return `${API_BASE_URL}${raw.startsWith('/') ? raw : `/${raw}`}`;
  }

  private normalizeProjectAttachment(item: ControlProjectAttachment): ProjectAttachmentItem {
    return {
      id: Number(item.id),
      tenant_id: Number(item.tenant_id),
      project_id: Number(item.project_id),
      file_name: String(item.file_name || 'arquivo'),
      file_path: String(item.file_path || ''),
      file_type: item.file_type || null,
      file_size: Number(item.file_size || 0),
      uploaded_by: Number(item.uploaded_by || 0),
      uploaded_at: item.uploaded_at,
      file_url: String(item.file_url || ''),
      is_image: !!item.is_image
    };
  }

  private loadProjectAttachments(projectId: number): void {
    if (!this.tenantId) {
      this.projectAttachments = [];
      return;
    }
    this.controlprojectService.getAttachments(this.tenantId, projectId).subscribe({
      next: (rows) => {
        this.projectAttachments = (rows || []).map((item) => this.normalizeProjectAttachment(item));
      },
      error: () => {
        this.projectAttachments = [];
      }
    });
  }

  openNewProjectModal() {
    this.editingDraftId = null;
    this.draftDateReason = '';
    this.draftOriginalDates = { start_date: '', end_date: '' };
    this.resetTapForm();
    if (this.userId) {
      this.newProjectForm.manager_user_id = String(this.userId);
    }
    if (this.userDepartmentId) {
      this.newProjectForm.department_id = String(this.userDepartmentId);
    }
    this.syncProjectDateFields();
    this.showNewProjectModal = true;
  }

  closeNewProjectModal() {
    this.showNewProjectModal = false;
    this.editingDraftId = null;
    this.draftDateReason = '';
    this.draftOriginalDates = { start_date: '', end_date: '' };
    this.resetProjectForm();
  }

  submitProject(asDraft = false) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    if (!this.newProjectForm.name.trim()) {
      return;
    }
    if (this.projectStartDateInvalid || this.projectEndDateInvalid) {
      return;
    }

    const status = asDraft
      ? 'DRAFT'
      : this.newProjectForm.status === 'DRAFT'
        ? 'PLANNED'
        : this.newProjectForm.status;
    const payload: any = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      user_id: this.userId,
      name: this.newProjectForm.name.trim(),
      description: this.newProjectForm.description.trim() || null,
      status,
      start_date: this.newProjectForm.start_date || null,
      end_date: this.newProjectForm.end_date || null,
      manager_user_id: this.newProjectForm.manager_user_id ? Number(this.newProjectForm.manager_user_id) : null,
      department_id: this.newProjectForm.department_id ? Number(this.newProjectForm.department_id) : null,
      tap: this.tapForm
    };

    if (this.editingDraftId) {
      const startChanged = (this.draftOriginalDates.start_date || '') !== (payload.start_date || '');
      const endChanged = (this.draftOriginalDates.end_date || '') !== (payload.end_date || '');
      if ((startChanged || endChanged) && !this.draftDateReason.trim()) {
        return;
      }
      if (startChanged || endChanged) {
        payload['date_change_reason'] = this.draftDateReason.trim();
      }
      this.controlprojectService.updateProject(this.editingDraftId, payload).subscribe({
        next: (updated) => {
          this.projects = this.projects.map((project) => (project.id === updated.id ? updated : project));
          this.closeNewProjectModal();
        }
      });
      return;
    }

    this.controlprojectService.createProject(payload).subscribe({
      next: (created) => {
        this.projects = [created, ...this.projects];
        this.closeNewProjectModal();
      }
    });
  }

  openProject(project: ControlProject) {
    if (project.status === 'DRAFT') {
      this.openDraftEditor(project);
      return;
    }
    this.editingProjectTitle = false;
    this.projectTitleDraft = '';
    this.editingProjectDescription = false;
    this.projectDescriptionDraft = '';
    this.projectDescriptionExpanded = false;
    this.savingProjectTitle = false;
    this.savingProjectDescription = false;
    this.activeProject = project;
    this.detailTab = 'calendar';
    this.persistActiveProjectId(project.id);
    this.loadProjectDetail(project.id);
  }

  deleteProject(project: ControlProject, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.tenantId) {
      return;
    }
    const projectName = project?.name?.trim();
    const label = projectName ? `"${projectName}"` : 'este projeto';
    const message = `Deseja excluir ${label}? Isso vai remover tarefas, chamados, anexos e histórico deste projeto. Esta ação não pode ser desfeita.`;
    if (!confirm(message)) {
      return;
    }
    this.controlprojectService.deleteProject(this.tenantId, project.id).subscribe({
      next: () => {
        this.projects = this.projects.filter((item) => item.id !== project.id);
        if (this.activeProject?.id === project.id) {
          this.clearPersistedActiveProjectId();
          this.activeProject = null;
          this.projectMembers = [];
          this.refreshProjectAssigneeOptions();
          this.tapVersions = [];
          this.milestones = [];
          this.tasks = [];
          this.visibleProjectTasks = [];
          this.tickets = [];
          this.history = [];
        }
      }
    });
  }

  private openDraftEditor(project: ControlProject) {
    this.editingDraftId = project.id;
    this.newProjectForm = {
      name: project.name || '',
      description: project.description || '',
      status: (project.status as ProjectStatus) || 'PLANNED',
      start_date: project.start_date ? toLocalISODate(new Date(project.start_date)) : '',
      end_date: project.end_date ? toLocalISODate(new Date(project.end_date)) : '',
      manager_user_id: project.manager_user_id ? String(project.manager_user_id) : '',
      department_id: project.department_id ? String(project.department_id) : ''
    };
    this.draftOriginalDates = {
      start_date: this.newProjectForm.start_date,
      end_date: this.newProjectForm.end_date
    };
    this.draftDateReason = '';
    this.syncProjectDateFields();
    if (this.tenantId) {
      this.controlprojectService.getTapVersions(this.tenantId, project.id).subscribe({
        next: (rows) => {
          const latest = rows?.[0];
          if (!latest) {
            this.resetTapForm();
            return;
          }
          this.tapForm = {
            title: latest.title || '',
            justification: latest.justification || '',
            objectives: latest.objectives || '',
            business_scope: latest.business_scope || '',
            technical_scope: latest.technical_scope || '',
            deliverables: latest.deliverables || '',
            non_deliverables: latest.non_deliverables || '',
            permissions: latest.permissions || '',
            constraints: latest.constraints || '',
            assumptions: latest.assumptions || '',
            risks: latest.risks || '',
            success_criteria: latest.success_criteria || '',
            budget: latest.budget || ''
          };
        },
        error: () => this.resetTapForm()
      });
    }
    this.showNewProjectModal = true;
  }

  onProjectStartDateInput(rawValue: string) {
    const normalized = this.normalizeBrDateInput(rawValue);
    this.projectStartDateDisplay = normalized;
    this.projectStartDateInvalid = false;

    if (!normalized) {
      this.newProjectForm.start_date = '';
      this.projectStartDatePickerValue = null;
      return;
    }

    if (normalized.length < 10) {
      this.newProjectForm.start_date = '';
      this.projectStartDatePickerValue = null;
      return;
    }

    const parsed = this.parseBrDate(normalized);
    if (!parsed) {
      this.newProjectForm.start_date = '';
      this.projectStartDatePickerValue = null;
      this.projectStartDateInvalid = true;
      return;
    }

    this.projectStartDatePickerValue = parsed;
    this.newProjectForm.start_date = this.toIsoDate(parsed);
  }

  onProjectStartDateBlur() {
    if (!this.projectStartDateDisplay) {
      this.projectStartDateInvalid = false;
      this.newProjectForm.start_date = '';
      this.projectStartDatePickerValue = null;
      return;
    }
    const parsed = this.parseBrDate(this.projectStartDateDisplay);
    if (!parsed) {
      this.projectStartDateInvalid = true;
      this.newProjectForm.start_date = '';
      this.projectStartDatePickerValue = null;
      return;
    }
    this.projectStartDateInvalid = false;
    this.projectStartDateDisplay = this.formatBrDate(parsed);
    this.projectStartDatePickerValue = parsed;
    this.newProjectForm.start_date = this.toIsoDate(parsed);
  }

  onProjectStartDatePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked) {
      return;
    }
    const normalized = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate());
    this.projectStartDateInvalid = false;
    this.projectStartDatePickerValue = normalized;
    this.projectStartDateDisplay = this.formatBrDate(normalized);
    this.newProjectForm.start_date = this.toIsoDate(normalized);
  }

  onProjectEndDateInput(rawValue: string) {
    const normalized = this.normalizeBrDateInput(rawValue);
    this.projectEndDateDisplay = normalized;
    this.projectEndDateInvalid = false;

    if (!normalized) {
      this.newProjectForm.end_date = '';
      this.projectEndDatePickerValue = null;
      return;
    }

    if (normalized.length < 10) {
      this.newProjectForm.end_date = '';
      this.projectEndDatePickerValue = null;
      return;
    }

    const parsed = this.parseBrDate(normalized);
    if (!parsed) {
      this.newProjectForm.end_date = '';
      this.projectEndDatePickerValue = null;
      this.projectEndDateInvalid = true;
      return;
    }

    this.projectEndDatePickerValue = parsed;
    this.newProjectForm.end_date = this.toIsoDate(parsed);
  }

  onProjectEndDateBlur() {
    if (!this.projectEndDateDisplay) {
      this.projectEndDateInvalid = false;
      this.newProjectForm.end_date = '';
      this.projectEndDatePickerValue = null;
      return;
    }
    const parsed = this.parseBrDate(this.projectEndDateDisplay);
    if (!parsed) {
      this.projectEndDateInvalid = true;
      this.newProjectForm.end_date = '';
      this.projectEndDatePickerValue = null;
      return;
    }
    this.projectEndDateInvalid = false;
    this.projectEndDateDisplay = this.formatBrDate(parsed);
    this.projectEndDatePickerValue = parsed;
    this.newProjectForm.end_date = this.toIsoDate(parsed);
  }

  onProjectEndDatePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked) {
      return;
    }
    const normalized = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate());
    this.projectEndDateInvalid = false;
    this.projectEndDatePickerValue = normalized;
    this.projectEndDateDisplay = this.formatBrDate(normalized);
    this.newProjectForm.end_date = this.toIsoDate(normalized);
  }

  onMilestoneDueDateInput(rawValue: string) {
    const normalized = this.normalizeBrDateInput(rawValue);
    this.milestoneDueDateDisplay = normalized;
    this.milestoneDueDateInvalid = false;

    if (!normalized) {
      this.newMilestoneForm.due_date = '';
      this.milestoneDueDatePickerValue = null;
      return;
    }

    if (normalized.length < 10) {
      this.newMilestoneForm.due_date = '';
      this.milestoneDueDatePickerValue = null;
      return;
    }

    const parsed = this.parseBrDate(normalized);
    if (!parsed) {
      this.newMilestoneForm.due_date = '';
      this.milestoneDueDatePickerValue = null;
      this.milestoneDueDateInvalid = true;
      return;
    }

    this.milestoneDueDateInvalid = false;
    this.milestoneDueDatePickerValue = parsed;
    this.newMilestoneForm.due_date = this.toIsoDate(parsed);
  }

  onMilestoneDueDateBlur() {
    if (!this.milestoneDueDateDisplay) {
      this.milestoneDueDateInvalid = false;
      this.newMilestoneForm.due_date = '';
      this.milestoneDueDatePickerValue = null;
      return;
    }
    const parsed = this.parseBrDate(this.milestoneDueDateDisplay);
    if (!parsed) {
      this.milestoneDueDateInvalid = true;
      this.newMilestoneForm.due_date = '';
      this.milestoneDueDatePickerValue = null;
      return;
    }
    this.milestoneDueDateInvalid = false;
    this.milestoneDueDateDisplay = this.formatBrDate(parsed);
    this.milestoneDueDatePickerValue = parsed;
    this.newMilestoneForm.due_date = this.toIsoDate(parsed);
  }

  onMilestoneDueDatePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked) {
      return;
    }
    const normalized = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate());
    this.milestoneDueDateInvalid = false;
    this.milestoneDueDatePickerValue = normalized;
    this.milestoneDueDateDisplay = this.formatBrDate(normalized);
    this.newMilestoneForm.due_date = this.toIsoDate(normalized);
  }

  onDateEditStartDateInput(rawValue: string) {
    const normalized = this.normalizeBrDateInput(rawValue);
    this.dateEditStartDateDisplay = normalized;
    this.dateEditStartDateInvalid = false;

    if (!normalized) {
      this.dateEditForm.start_date = '';
      this.dateEditStartDatePickerValue = null;
      return;
    }

    if (normalized.length < 10) {
      this.dateEditForm.start_date = '';
      this.dateEditStartDatePickerValue = null;
      return;
    }

    const parsed = this.parseBrDate(normalized);
    if (!parsed) {
      this.dateEditForm.start_date = '';
      this.dateEditStartDatePickerValue = null;
      this.dateEditStartDateInvalid = true;
      return;
    }

    this.dateEditStartDateInvalid = false;
    this.dateEditStartDatePickerValue = parsed;
    this.dateEditForm.start_date = this.toIsoDate(parsed);
  }

  onDateEditStartDateBlur() {
    if (!this.dateEditStartDateDisplay) {
      this.dateEditStartDateInvalid = false;
      this.dateEditForm.start_date = '';
      this.dateEditStartDatePickerValue = null;
      return;
    }
    const parsed = this.parseBrDate(this.dateEditStartDateDisplay);
    if (!parsed) {
      this.dateEditStartDateInvalid = true;
      this.dateEditForm.start_date = '';
      this.dateEditStartDatePickerValue = null;
      return;
    }
    this.dateEditStartDateInvalid = false;
    this.dateEditStartDateDisplay = this.formatBrDate(parsed);
    this.dateEditStartDatePickerValue = parsed;
    this.dateEditForm.start_date = this.toIsoDate(parsed);
  }

  onDateEditStartDatePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked) {
      return;
    }
    const normalized = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate());
    this.dateEditStartDateInvalid = false;
    this.dateEditStartDatePickerValue = normalized;
    this.dateEditStartDateDisplay = this.formatBrDate(normalized);
    this.dateEditForm.start_date = this.toIsoDate(normalized);
  }

  onDateEditEndDateInput(rawValue: string) {
    const normalized = this.normalizeBrDateInput(rawValue);
    this.dateEditEndDateDisplay = normalized;
    this.dateEditEndDateInvalid = false;

    if (!normalized) {
      this.dateEditForm.end_date = '';
      this.dateEditEndDatePickerValue = null;
      return;
    }

    if (normalized.length < 10) {
      this.dateEditForm.end_date = '';
      this.dateEditEndDatePickerValue = null;
      return;
    }

    const parsed = this.parseBrDate(normalized);
    if (!parsed) {
      this.dateEditForm.end_date = '';
      this.dateEditEndDatePickerValue = null;
      this.dateEditEndDateInvalid = true;
      return;
    }

    this.dateEditEndDateInvalid = false;
    this.dateEditEndDatePickerValue = parsed;
    this.dateEditForm.end_date = this.toIsoDate(parsed);
  }

  onDateEditEndDateBlur() {
    if (!this.dateEditEndDateDisplay) {
      this.dateEditEndDateInvalid = false;
      this.dateEditForm.end_date = '';
      this.dateEditEndDatePickerValue = null;
      return;
    }
    const parsed = this.parseBrDate(this.dateEditEndDateDisplay);
    if (!parsed) {
      this.dateEditEndDateInvalid = true;
      this.dateEditForm.end_date = '';
      this.dateEditEndDatePickerValue = null;
      return;
    }
    this.dateEditEndDateInvalid = false;
    this.dateEditEndDateDisplay = this.formatBrDate(parsed);
    this.dateEditEndDatePickerValue = parsed;
    this.dateEditForm.end_date = this.toIsoDate(parsed);
  }

  onDateEditEndDatePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked) {
      return;
    }
    const normalized = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate());
    this.dateEditEndDateInvalid = false;
    this.dateEditEndDatePickerValue = normalized;
    this.dateEditEndDateDisplay = this.formatBrDate(normalized);
    this.dateEditForm.end_date = this.toIsoDate(normalized);
  }

  onTaskStartDateInput(rawValue: string) {
    const normalized = this.normalizeBrDateInput(rawValue);
    this.taskStartDateDisplay = normalized;
    this.taskStartDateInvalid = false;

    if (!normalized) {
      this.taskForm.patchValue({ start_date: '' }, { emitEvent: false });
      this.taskStartDatePickerValue = null;
      return;
    }

    if (normalized.length !== 10) {
      this.taskForm.patchValue({ start_date: '' }, { emitEvent: false });
      this.taskStartDatePickerValue = null;
      return;
    }

    const parsed = this.parseBrDate(normalized);
    if (!parsed) {
      this.taskForm.patchValue({ start_date: '' }, { emitEvent: false });
      this.taskStartDatePickerValue = null;
      this.taskStartDateInvalid = true;
      return;
    }

    this.taskStartDateInvalid = false;
    this.taskStartDatePickerValue = parsed;
    this.taskForm.patchValue({ start_date: this.toIsoDate(parsed) }, { emitEvent: false });
  }

  onTaskStartDateBlur() {
    if (!this.taskStartDateDisplay) {
      this.taskStartDateInvalid = false;
      this.taskForm.patchValue({ start_date: '' }, { emitEvent: false });
      this.taskStartDatePickerValue = null;
      return;
    }
    const parsed = this.parseBrDate(this.taskStartDateDisplay);
    if (!parsed) {
      this.taskStartDateInvalid = true;
      this.taskForm.patchValue({ start_date: '' }, { emitEvent: false });
      this.taskStartDatePickerValue = null;
      return;
    }
    this.taskStartDateInvalid = false;
    this.taskStartDateDisplay = this.formatBrDate(parsed);
    this.taskStartDatePickerValue = parsed;
    this.taskForm.patchValue({ start_date: this.toIsoDate(parsed) }, { emitEvent: false });
  }

  onTaskStartDatePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked) {
      return;
    }
    const normalized = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate());
    this.taskStartDateInvalid = false;
    this.taskStartDatePickerValue = normalized;
    this.taskStartDateDisplay = this.formatBrDate(normalized);
    this.taskForm.patchValue({ start_date: this.toIsoDate(normalized) }, { emitEvent: false });
  }

  onTaskDueDateInput(rawValue: string) {
    const normalized = this.normalizeBrDateInput(rawValue);
    this.taskDueDateDisplay = normalized;
    this.taskDueDateInvalid = false;

    if (!normalized) {
      this.taskForm.patchValue({ due_date: '' }, { emitEvent: false });
      this.taskDueDatePickerValue = null;
      return;
    }

    if (normalized.length !== 10) {
      this.taskForm.patchValue({ due_date: '' }, { emitEvent: false });
      this.taskDueDatePickerValue = null;
      return;
    }

    const parsed = this.parseBrDate(normalized);
    if (!parsed) {
      this.taskForm.patchValue({ due_date: '' }, { emitEvent: false });
      this.taskDueDatePickerValue = null;
      this.taskDueDateInvalid = true;
      return;
    }

    this.taskDueDateInvalid = false;
    this.taskDueDatePickerValue = parsed;
    this.taskForm.patchValue({ due_date: this.toIsoDate(parsed) }, { emitEvent: false });
  }

  onTaskDueDateBlur() {
    if (!this.taskDueDateDisplay) {
      this.taskDueDateInvalid = false;
      this.taskForm.patchValue({ due_date: '' }, { emitEvent: false });
      this.taskDueDatePickerValue = null;
      return;
    }
    const parsed = this.parseBrDate(this.taskDueDateDisplay);
    if (!parsed) {
      this.taskDueDateInvalid = true;
      this.taskForm.patchValue({ due_date: '' }, { emitEvent: false });
      this.taskDueDatePickerValue = null;
      return;
    }
    this.taskDueDateInvalid = false;
    this.taskDueDateDisplay = this.formatBrDate(parsed);
    this.taskDueDatePickerValue = parsed;
    this.taskForm.patchValue({ due_date: this.toIsoDate(parsed) }, { emitEvent: false });
  }

  onTaskDueDatePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked) {
      return;
    }
    const normalized = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate());
    this.taskDueDateInvalid = false;
    this.taskDueDatePickerValue = normalized;
    this.taskDueDateDisplay = this.formatBrDate(normalized);
    this.taskForm.patchValue({ due_date: this.toIsoDate(normalized) }, { emitEvent: false });
  }

  closeProject() {
    this.goToProjectMainPanel();
  }

  goToProjectMainPanel() {
    this.editingProjectTitle = false;
    this.projectTitleDraft = '';
    this.editingProjectDescription = false;
    this.projectDescriptionDraft = '';
    this.projectDescriptionExpanded = false;
    this.savingProjectTitle = false;
    this.savingProjectDescription = false;
    this.clearPersistedActiveProjectId();
    this.activeProject = null;
    this.activeMainTab = 'dashboard';
    this.statusFilters = ['IN_PROGRESS'];
    this.projectCurrentPage = 1;
  }

  startProjectTitleEdit() {
    if (!this.activeProject || this.savingProjectTitle) {
      return;
    }
    this.projectTitleDraft = String(this.activeProject.name || '').trim();
    this.editingProjectTitle = true;
  }

  startProjectDescriptionEdit() {
    if (!this.activeProject || this.savingProjectDescription) {
      return;
    }
    this.projectDescriptionDraft = String(this.activeProject.description || '').trim();
    this.editingProjectDescription = true;
  }

  cancelProjectTitleEdit() {
    if (this.savingProjectTitle) {
      return;
    }
    this.editingProjectTitle = false;
    this.projectTitleDraft = '';
  }

  cancelProjectDescriptionEdit() {
    if (this.savingProjectDescription) {
      return;
    }
    this.editingProjectDescription = false;
    this.projectDescriptionDraft = '';
  }

  toggleProjectDescriptionExpanded() {
    if (!this.activeProject || this.editingProjectDescription) {
      return;
    }
    this.projectDescriptionExpanded = !this.projectDescriptionExpanded;
  }

  get hasProjectDescription() {
    return !!String(this.activeProject?.description || '').trim();
  }

  get showProjectDescriptionToggle() {
    const description = String(this.activeProject?.description || '').trim();
    return description.length > 220 || description.includes('\n');
  }

  saveProjectTitleEdit() {
    if (!this.activeProject || !this.tenantId || this.savingProjectTitle) {
      return;
    }
    const nextName = String(this.projectTitleDraft || '').trim();
    if (!nextName) {
      return;
    }
    const currentName = String(this.activeProject.name || '').trim();
    if (nextName === currentName) {
      this.cancelProjectTitleEdit();
      return;
    }

    this.savingProjectTitle = true;
    const payload: any = {
      tenant_id: this.tenantId,
      name: nextName
    };
    if (this.userId) {
      payload.user_id = this.userId;
    }

    this.controlprojectService.updateProject(this.activeProject.id, payload).subscribe({
      next: (updated) => {
        this.activeProject = updated;
        this.projects = this.projects.map((project) => (project.id === updated.id ? updated : project));
        this.savingProjectTitle = false;
        this.editingProjectTitle = false;
        this.projectTitleDraft = '';
        this.loadProjectHistory(updated.id);
      },
      error: () => {
        this.savingProjectTitle = false;
      }
    });
  }

  saveProjectDescriptionEdit() {
    if (!this.activeProject || !this.tenantId || this.savingProjectDescription) {
      return;
    }
    const nextDescription = String(this.projectDescriptionDraft || '').trim();
    const currentDescription = String(this.activeProject.description || '').trim();
    if (nextDescription === currentDescription) {
      this.cancelProjectDescriptionEdit();
      return;
    }

    this.savingProjectDescription = true;
    const payload: any = {
      tenant_id: this.tenantId,
      description: nextDescription || null
    };
    if (this.userId) {
      payload.user_id = this.userId;
    }

    this.controlprojectService.updateProject(this.activeProject.id, payload).subscribe({
      next: (updated) => {
        this.activeProject = updated;
        this.projects = this.projects.map((project) => (project.id === updated.id ? updated : project));
        this.savingProjectDescription = false;
        this.editingProjectDescription = false;
        this.projectDescriptionDraft = '';
        this.projectDescriptionExpanded = true;
        this.loadProjectHistory(updated.id);
      },
      error: () => {
        this.savingProjectDescription = false;
      }
    });
  }

  updateProjectStatus(status: ProjectStatus) {
    if (!this.activeProject || !this.tenantId) {
      return;
    }
    const projectId = this.activeProject.id;
    const previousStatus = this.activeProject.status as ProjectStatus;
    this.activeProject = { ...this.activeProject, status };
    const payload: any = { tenant_id: this.tenantId, status };
    if (this.userId) {
      payload.user_id = this.userId;
    }
    this.controlprojectService.updateProject(projectId, payload).subscribe({
      next: (updated) => {
        this.activeProject = updated;
        this.projects = this.projects.map((item) => (item.id === updated.id ? updated : item));
        this.loadProjectHistory(updated.id);
      },
      error: () => {
        this.activeProject = { ...this.activeProject!, status: previousStatus };
      }
    });
  }

  openDateEditModal() {
    if (!this.activeProject) {
      return;
    }
    this.dateEditForm = {
      start_date: this.activeProject.start_date ? toLocalISODate(new Date(this.activeProject.start_date)) : '',
      end_date: this.activeProject.end_date ? toLocalISODate(new Date(this.activeProject.end_date)) : '',
      reason: ''
    };
    this.syncDateEditDateFields();
    this.showDateEditModal = true;
  }

  closeDateEditModal() {
    this.showDateEditModal = false;
  }

  submitDateChange() {
    if (!this.activeProject || !this.tenantId || !this.userId) {
      return;
    }
    if (this.dateEditStartDateInvalid || this.dateEditEndDateInvalid) {
      return;
    }
    if (!this.dateEditForm.reason.trim()) {
      return;
    }
    const normalizeDate = (value: string) => {
      if (!value) {
        return null;
      }
      const localDate = new Date(`${value}T00:00:00`);
      if (Number.isNaN(localDate.getTime())) {
        return value;
      }
      return toLocalSQLDateTime(localDate);
    };
    const payload = {
      tenant_id: this.tenantId,
      user_id: this.userId,
      start_date: normalizeDate(this.dateEditForm.start_date),
      end_date: normalizeDate(this.dateEditForm.end_date),
      date_change_reason: this.dateEditForm.reason.trim()
    };
    this.controlprojectService.updateProject(this.activeProject.id, payload).subscribe({
      next: (updated) => {
        this.activeProject = updated;
        this.projects = this.projects.map((project) => (project.id === updated.id ? updated : project));
        this.closeDateEditModal();
        this.loadProjectHistory(updated.id);
      }
    });
  }

  openTapModal() {
    this.editingTapId = null;
    this.resetTapForm();
    this.showTapModal = true;
  }

  closeTapModal() {
    this.showTapModal = false;
    this.editingTapId = null;
  }

  submitTapVersion() {
    if (!this.activeProject || !this.tenantId || !this.userId) {
      return;
    }
    const payload = { ...this.tapForm, tenant_id: this.tenantId, created_by: this.userId };
    if (this.editingTapId) {
      this.controlprojectService.updateTapVersion(this.activeProject.id, this.editingTapId, payload).subscribe({
        next: (updated) => {
          this.tapVersions = this.tapVersions.map((tap) => (tap.id === updated.id ? updated : tap));
          this.closeTapModal();
          this.resetTapForm();
        }
      });
      return;
    }

    this.controlprojectService.createTapVersion(this.activeProject.id, payload).subscribe({
      next: (created) => {
        this.tapVersions = [created, ...this.tapVersions];
        this.closeTapModal();
        this.resetTapForm();
      }
    });
  }

  openTapEdit(tap: ControlProjectTapVersion) {
    this.editingTapId = tap.id;
    this.tapForm = {
      title: tap.title || '',
      justification: tap.justification || '',
      objectives: tap.objectives || '',
      business_scope: tap.business_scope || '',
      technical_scope: tap.technical_scope || '',
      deliverables: tap.deliverables || '',
      non_deliverables: tap.non_deliverables || '',
      permissions: tap.permissions || '',
      constraints: tap.constraints || '',
      assumptions: tap.assumptions || '',
      risks: tap.risks || '',
      success_criteria: tap.success_criteria || '',
      budget: tap.budget || ''
    };
    this.showTapModal = true;
  }

  private tapFieldKey(tapId: number, field: string): string {
    return `${tapId}:${field}`;
  }

  isTapFieldExpanded(tapId: number, field: string): boolean {
    return !!this.tapExpandedFields[this.tapFieldKey(tapId, field)];
  }

  toggleTapFieldExpanded(tapId: number, field: string): void {
    const key = this.tapFieldKey(tapId, field);
    this.tapExpandedFields[key] = !this.tapExpandedFields[key];
  }

  canTapFieldExpand(value: string | null | undefined): boolean {
    return (value || '').trim().length > 180;
  }

  openMemberModal() {
    this.memberSearch = '';
    this.showMemberModal = true;
  }

  closeMemberModal() {
    this.memberSearch = '';
    this.showMemberModal = false;
  }

  filteredMemberUsers() {
    const term = this.normalizeMemberSearch(this.memberSearch);
    if (!term) {
      return this.users;
    }
    return this.users.filter((user) => this.normalizeMemberSearch(user.name).includes(term));
  }

  private refreshProjectAssigneeOptions() {
    const seenUserIds = new Set<number>();
    this.projectAssigneeOptions = this.projectMembers
      .filter((member) => {
        const userId = Number(member.user_id);
        if (!userId || seenUserIds.has(userId)) {
          return false;
        }
        seenUserIds.add(userId);
        return true;
      })
      .map((member) => ({
        id: Number(member.user_id),
        name: String(
          member.user_name
          || this.users.find((user) => Number(user.id) === Number(member.user_id))?.name
          || ''
        ).trim()
      }))
      .filter((member) => !!member.name)
      .sort((left, right) => left.name.localeCompare(right.name, 'pt-BR'));
    this.refreshTaskAssigneeOptions();
    this.refreshTaskDetailAssigneeOptions();
  }

  onTaskAssigneeSearchChange(value: string, detail = false) {
    if (detail) {
      this.taskDetailAssigneeSearch = value;
      this.refreshTaskDetailAssigneeOptions();
      return;
    }
    this.taskAssigneeSearch = value;
    this.refreshTaskAssigneeOptions();
  }

  private refreshTaskAssigneeOptions() {
    this.filteredTaskAssigneeOptions = this.filterProjectAssigneeOptions(this.taskAssigneeSearch);
  }

  private refreshTaskDetailAssigneeOptions() {
    this.filteredTaskDetailAssigneeOptions = this.filterProjectAssigneeOptions(this.taskDetailAssigneeSearch);
  }

  private filterProjectAssigneeOptions(search: string) {
    const term = this.normalizeMemberSearch(search);
    if (!term) {
      return this.projectAssigneeOptions;
    }
    return this.projectAssigneeOptions.filter((member) =>
      this.normalizeMemberSearch(member.name).includes(term)
    );
  }

  onTaskAssigneeSelectOpened(opened: boolean, detail = false) {
    if (!opened) {
      if (detail) {
        this.taskDetailAssigneeSearch = '';
        this.refreshTaskDetailAssigneeOptions();
      } else {
        this.taskAssigneeSearch = '';
        this.refreshTaskAssigneeOptions();
      }
      return;
    }
    window.setTimeout(() => {
      const target = detail ? this.taskDetailAssigneeSearchInput : this.taskAssigneeSearchInput;
      target?.nativeElement.focus();
    }, 0);
  }

  onMemberSelectOpened(opened: boolean) {
    if (!opened) {
      this.memberSearch = '';
      return;
    }
    window.setTimeout(() => this.memberSearchInput?.nativeElement.focus(), 0);
  }

  private normalizeMemberSearch(value: string | null | undefined) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  submitMember() {
    if (!this.activeProject || !this.tenantId || !this.userId) {
      return;
    }
    if (!this.newMemberForm.user_id) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      user_id: Number(this.newMemberForm.user_id),
      role: this.newMemberForm.role,
      added_by: this.userId
    };
    this.controlprojectService.addMember(this.activeProject.id, payload).subscribe({
      next: (rows) => {
        this.projectMembers = rows;
        this.refreshProjectAssigneeOptions();
        this.closeMemberModal();
        this.newMemberForm = { user_id: '', role: 'MEMBER' };
      }
    });
  }

  removeMember(member: ControlProjectMember) {
    if (!this.activeProject || !this.tenantId) {
      return;
    }
    this.controlprojectService.removeMember(this.tenantId, this.activeProject.id, member.id).subscribe({
      next: () => {
        this.projectMembers = this.projectMembers.filter((m) => m.id !== member.id);
        this.refreshProjectAssigneeOptions();
      }
    });
  }

  openMilestoneModal() {
    this.syncMilestoneDateFields();
    this.showMilestoneModal = true;
  }

  closeMilestoneModal() {
    this.showMilestoneModal = false;
  }

  submitMilestone() {
    if (!this.activeProject || !this.tenantId || !this.userId) {
      return;
    }
    if (this.milestoneDueDateInvalid || !this.newMilestoneForm.title.trim() || !this.newMilestoneForm.due_date) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      title: this.newMilestoneForm.title.trim(),
      description: this.newMilestoneForm.description.trim() || null,
      due_date: this.newMilestoneForm.due_date,
      status: this.newMilestoneForm.status
    };
    this.controlprojectService.createMilestone(this.activeProject.id, payload).subscribe({
      next: (created) => {
        this.milestones = [...this.milestones, created].sort((a, b) => a.due_date.localeCompare(b.due_date));
        this.closeMilestoneModal();
        this.newMilestoneForm = { title: '', description: '', due_date: '', status: 'PLANNED' };
        this.syncMilestoneDateFields();
      }
    });
  }

  deleteMilestone(milestone: ControlProjectMilestone, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.activeProject || !this.tenantId) {
      return;
    }
    this.milestoneToDelete = milestone;
    this.showMilestoneDeleteModal = true;
  }

  cancelMilestoneDelete() {
    this.showMilestoneDeleteModal = false;
    this.milestoneToDelete = null;
  }

  confirmMilestoneDelete() {
    if (!this.activeProject || !this.tenantId || !this.milestoneToDelete) {
      return;
    }
    const milestone = this.milestoneToDelete;
    this.controlprojectService.deleteMilestone(this.tenantId, this.activeProject.id, milestone.id).subscribe({
      next: () => {
        this.milestones = this.milestones.filter((item) => item.id !== milestone.id);
        this.cancelMilestoneDelete();
      }
    });
  }

  openTaskModal() {
    if (!this.activeProject) {
      return;
    }
    this.taskForm.reset({
      title: '',
      description: '',
      priority: 'MEDIUM',
      visibility: 'PRIVATE',
      status: 'OPEN',
      start_date: '',
      due_date: '',
      subgroupId: '',
      bucketId: '',
      groupId: '',
      tagIds: [],
      assigneeId: '',
      newSubtask: '',
      inlineBucketName: '',
      inlineBucketDescription: ''
    });
    this.handleGroupSelectionChange();
    this.handleSubgroupSelectionChange();
    this.taskStartDateDisplay = '';
    this.taskDueDateDisplay = '';
    this.taskStartDatePickerValue = null;
    this.taskDueDatePickerValue = null;
    this.taskStartDateInvalid = false;
    this.taskDueDateInvalid = false;
    this.draftSubtasks = [];
    this.newTaskFiles = [];
    this.taskModalDragging = false;
    this.taskAssigneeSearch = '';
    this.refreshTaskAssigneeOptions();
    this.showTaskModal = true;
  }

  closeTaskModal() {
    this.showTaskModal = false;
    this.clearTaskFilePreviews();
    this.newTaskFiles = [];
    this.taskModalDragging = false;
    this.taskAssigneeSearch = '';
    this.refreshTaskAssigneeOptions();
  }

  addDraftSubtask() {
    const text = (this.taskForm.get('newSubtask')?.value || '').trim();
    if (!text) {
      return;
    }
    this.draftSubtasks.push(text);
    this.taskForm.get('newSubtask')?.reset('');
  }

  removeDraftSubtask(index: number) {
    this.draftSubtasks.splice(index, 1);
  }

  submitTask() {
    if (!this.taskForm.valid || !this.tenantId || !this.userId || !this.activeProject) {
      return;
    }
    if (this.taskStartDateInvalid || this.taskDueDateInvalid) {
      return;
    }
    const value = this.taskForm.value;
    const payload = {
      tenant_id: this.tenantId,
      title: value.title,
      description: value.description,
      priority: value.priority,
      visibility: value.visibility,
      status: value.status,
      due_date: value.due_date || null,
      start_date: value.start_date || null,
      created_by: this.userId,
      subgroup_id: value.subgroupId || null,
      bucket_id: value.bucketId || null,
      groups: value.groupId ? [Number(value.groupId)] : [],
      tags: value.tagIds || [],
      assignees: value.assigneeId ? [Number(value.assigneeId)] : [],
      subtasks: this.draftSubtasks,
      task_code: this.generateTaskCode(),
      project_id: this.activeProject.id
    };
    this.controltaskService.createTask(payload).subscribe({
      next: (response) => {
        const taskId = response?.id;
        if (taskId && this.newTaskFiles.length) {
          this.uploadTaskAttachments(taskId);
          return;
        }
        this.showTaskModal = false;
        this.clearTaskFilePreviews();
        this.newTaskFiles = [];
        this.loadProjectDetail(this.activeProject?.id || 0);
      }
    });
  }

  get availableFormSubgroups() {
    const selectedGroup = Number(this.taskForm.get('groupId')?.value) || null;
    if (!selectedGroup) {
      return this.subgroups;
    }
    return this.subgroups.filter((subgroup) => subgroup.group_id === selectedGroup);
  }

  get availableFormBuckets() {
    const subgroupId = Number(this.taskForm.get('subgroupId')?.value) || null;
    if (!subgroupId) {
      return [];
    }
    return this.buckets.filter((bucket) => bucket.subgroup_id === subgroupId);
  }

  handleGroupSelectionChange() {
    const selectedGroup = Number(this.taskForm.get('groupId')?.value) || null;
    if (!selectedGroup) {
      this.taskForm.patchValue({ subgroupId: '', bucketId: '', inlineBucketName: '', inlineBucketDescription: '' });
      return;
    }
    const currentSubgroup = Number(this.taskForm.get('subgroupId')?.value) || null;
    if (currentSubgroup) {
      const subgroup = this.subgroups.find((item) => item.id === currentSubgroup);
      if (!subgroup || subgroup.group_id !== selectedGroup) {
        this.taskForm.patchValue({ subgroupId: '', bucketId: '', inlineBucketName: '', inlineBucketDescription: '' });
      }
    }
  }

  handleSubgroupSelectionChange() {
    const subgroupId = Number(this.taskForm.get('subgroupId')?.value) || null;
    if (!subgroupId) {
      this.taskForm.patchValue({ bucketId: '', inlineBucketName: '', inlineBucketDescription: '' });
      return;
    }
    const availableBuckets = this.buckets.filter((bucket) => bucket.subgroup_id === subgroupId);
    const currentBucket = Number(this.taskForm.get('bucketId')?.value) || null;
    if (currentBucket) {
      const bucket = this.buckets.find((item) => item.id === currentBucket);
      if (!bucket || bucket.subgroup_id !== subgroupId) {
        this.taskForm.patchValue({ bucketId: '' });
      }
    }
    if (!currentBucket && availableBuckets.length) {
      this.taskForm.patchValue({ bucketId: availableBuckets[0].id, inlineBucketName: '', inlineBucketDescription: '' });
    }
  }

  createInlineBucket() {
    if (!this.tenantId) {
      return;
    }
    const subgroupId = Number(this.taskForm.get('subgroupId')?.value) || null;
    const bucketName = (this.taskForm.get('inlineBucketName')?.value || '').trim();
    const description = (this.taskForm.get('inlineBucketDescription')?.value || '').trim();
    if (!subgroupId || !bucketName) {
      return;
    }
    this.inlineBucketLoading = true;
    const payload = {
      tenant_id: this.tenantId,
      subgroup_id: subgroupId,
      name: bucketName,
      description: description || null,
      order_index: 0
    };
    this.controltaskService.createBucket(payload).subscribe({
      next: (created: any) => {
        this.inlineBucketLoading = false;
        this.buckets = [...this.buckets, created];
        this.taskForm.patchValue({
          bucketId: created.id,
          inlineBucketName: '',
          inlineBucketDescription: ''
        });
      },
      error: () => {
        this.inlineBucketLoading = false;
      }
    });
  }

  openTaskDetail(task: ControlTask) {
    this.activeTask = task;
    this.taskDetailForm.reset({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      visibility: task.visibility,
      start_date: task.start_date ? this.toInputDate(task.start_date) : '',
      due_date: task.due_date ? this.toInputDate(task.due_date) : '',
      groupId: task.groups?.[0]?.id || '',
      subgroupId: task.subgroup_id || '',
      bucketId: task.bucket_id || '',
      tagIds: (task.tags || []).map((tag: any) => tag.id),
      assigneeId: this.taskResponsibleId(task)
    });
    this.syncTaskDetailDateFields();
    this.handleDetailGroupChange();
    this.handleDetailSubgroupChange();
    this.detailSubtaskText = '';
    this.detailCommentText = '';
    this.detailCommentFile = null;
    this.taskDetailAssigneeSearch = '';
    this.refreshTaskDetailAssigneeOptions();
    this.showTaskDetailModal = true;
  }

  closeTaskDetail() {
    this.showTaskDetailModal = false;
    this.activeTask = null;
    this.detailCommentFile = null;
    this.taskDetailStartDateDisplay = '';
    this.taskDetailDueDateDisplay = '';
    this.taskDetailStartDatePickerValue = null;
    this.taskDetailDueDatePickerValue = null;
    this.taskDetailStartDateInvalid = false;
    this.taskDetailDueDateInvalid = false;
    this.taskDetailAssigneeSearch = '';
  }

  deleteTask(task: ControlTask) {
    if (!this.tenantId) {
      return;
    }
    const label = task?.title?.trim() ? `"${task.title.trim()}"` : 'esta tarefa';
    if (!confirm(`Deseja excluir ${label}? Esta ação não pode ser desfeita.`)) {
      return;
    }
    this.controltaskService.deleteTask(task.id, this.tenantId).subscribe({
      next: () => {
        this.tasks = this.tasks.filter((item) => item.id !== task.id);
        this.refreshVisibleProjectTasks();
        if (this.activeTask?.id === task.id) {
          this.closeTaskDetail();
        }
        if (this.activeProject) {
          this.loadProjectDetail(this.activeProject.id);
        }
      }
    });
  }

  onTaskFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []);
    if (files.length) {
      this.newTaskFiles = [...this.newTaskFiles, ...files];
    }
    if (input) {
      input.value = '';
    }
  }

  removeTaskFile(index: number) {
    const removed = this.newTaskFiles[index];
    if (removed) {
      this.revokeTaskFilePreview(removed);
    }
    this.newTaskFiles = this.newTaskFiles.filter((_item, i) => i !== index);
  }

  onTaskDragOver(event: DragEvent) {
    event.preventDefault();
    this.taskModalDragging = true;
  }

  onTaskDragLeave() {
    this.taskModalDragging = false;
  }

  onTaskDrop(event: DragEvent) {
    event.preventDefault();
    this.taskModalDragging = false;
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length) {
      this.newTaskFiles = [...this.newTaskFiles, ...files];
    }
  }

  private uploadTaskAttachments(taskId: number) {
    if (!this.userId || !this.newTaskFiles.length) {
      this.showTaskModal = false;
      this.clearTaskFilePreviews();
      this.newTaskFiles = [];
      this.loadProjectDetail(this.activeProject?.id || 0);
      return;
    }
    const uploads = this.newTaskFiles.map((file) => this.controltaskService.uploadAttachment(taskId, file, this.userId as number));
    forkJoin(uploads.length ? uploads : [of(null)]).subscribe({
      next: () => {
        this.showTaskModal = false;
        this.clearTaskFilePreviews();
        this.newTaskFiles = [];
        this.loadProjectDetail(this.activeProject?.id || 0);
      },
      error: () => {
        this.showTaskModal = false;
        this.clearTaskFilePreviews();
        this.newTaskFiles = [];
        this.loadProjectDetail(this.activeProject?.id || 0);
      }
    });
  }

  isTaskImageFile(file: File) {
    return String(file?.type || '').toLowerCase().startsWith('image/');
  }

  taskFilePreviewUrl(file: File) {
    if (!this.isTaskImageFile(file)) {
      return '';
    }
    const existing = this.taskFilePreviewUrls.get(file);
    if (existing) {
      return existing;
    }
    const next = URL.createObjectURL(file);
    this.taskFilePreviewUrls.set(file, next);
    return next;
  }

  taskFileTypeLabel(file: File) {
    const mime = String(file?.type || '').trim();
    if (mime) {
      const [, subtype = mime] = mime.split('/');
      return subtype.toUpperCase();
    }
    const name = String(file?.name || '');
    const ext = name.includes('.') ? name.split('.').pop() : '';
    return ext ? ext.toUpperCase() : 'ARQUIVO';
  }

  taskFileSizeLabel(file: File) {
    const size = Number(file?.size || 0);
    if (!size) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = size;
    let index = 0;
    while (value >= 1024 && index < units.length - 1) {
      value /= 1024;
      index += 1;
    }
    const decimals = value >= 10 || index === 0 ? 0 : 1;
    return `${value.toFixed(decimals)} ${units[index]}`;
  }

  private revokeTaskFilePreview(file: File) {
    const preview = this.taskFilePreviewUrls.get(file);
    if (!preview) {
      return;
    }
    URL.revokeObjectURL(preview);
    this.taskFilePreviewUrls.delete(file);
  }

  private clearTaskFilePreviews() {
    this.newTaskFiles.forEach((file) => this.revokeTaskFilePreview(file));
  }

  private refreshDetailFormSubgroups() {
    const selectedGroup = Number(this.taskDetailForm.get('groupId')?.value) || null;
    this.detailFormSubgroups = selectedGroup
      ? this.subgroups.filter((subgroup) => subgroup.group_id === selectedGroup)
      : this.subgroups;
  }

  private refreshDetailFormBuckets() {
    const subgroupId = Number(this.taskDetailForm.get('subgroupId')?.value) || null;
    this.detailFormBuckets = subgroupId
      ? this.buckets.filter((bucket) => bucket.subgroup_id === subgroupId)
      : [];
  }

  handleDetailGroupChange() {
    const selectedGroup = Number(this.taskDetailForm.get('groupId')?.value) || null;
    this.refreshDetailFormSubgroups();
    if (!selectedGroup) {
      this.taskDetailForm.patchValue({ subgroupId: '', bucketId: '' });
      this.detailFormBuckets = [];
      return;
    }
    const subgroupId = Number(this.taskDetailForm.get('subgroupId')?.value) || null;
    if (subgroupId) {
      const subgroup = this.subgroups.find((item) => item.id === subgroupId);
      if (!subgroup || subgroup.group_id !== selectedGroup) {
        this.taskDetailForm.patchValue({ subgroupId: '', bucketId: '' });
      }
    }
    this.refreshDetailFormBuckets();
  }

  handleDetailSubgroupChange() {
    const subgroupId = Number(this.taskDetailForm.get('subgroupId')?.value) || null;
    this.refreshDetailFormBuckets();
    if (!subgroupId) {
      this.taskDetailForm.patchValue({ bucketId: '' });
      return;
    }
    const available = this.buckets.filter((bucket) => bucket.subgroup_id === subgroupId);
    if (!available.length) {
      this.taskDetailForm.patchValue({ bucketId: '' });
      return;
    }
    const currentBucket = Number(this.taskDetailForm.get('bucketId')?.value) || null;
    if (!currentBucket || !available.some((bucket) => bucket.id === currentBucket)) {
      this.taskDetailForm.patchValue({ bucketId: available[0].id });
    }
  }

  submitTaskDetail() {
    if (!this.activeTask) {
      return;
    }
    if (this.taskDetailStartDateInvalid || this.taskDetailDueDateInvalid) {
      return;
    }
    const value = this.taskDetailForm.value;
    const payload = {
      tenant_id: this.tenantId,
      user_id: this.userId,
      title: value.title,
      description: value.description,
      status: value.status,
      priority: value.priority,
      visibility: value.visibility,
      start_date: value.start_date || null,
      due_date: value.due_date || null,
      subgroup_id: value.subgroupId || null,
      bucket_id: value.bucketId || null,
      groups: value.groupId ? [Number(value.groupId)] : [],
      tags: (value.tagIds || []).map((tagId: any) => Number(tagId)).filter(Boolean),
      assignees: value.assigneeId ? [Number(value.assigneeId)] : []
    };
    this.controltaskService.updateTask(this.activeTask.id, payload).subscribe({
      next: () => {
        this.closeTaskDetail();
        if (this.activeProject) {
          this.loadProjectDetail(this.activeProject.id);
        }
      }
    });
  }

  onTaskDetailStartDateInput(rawValue: string) {
    const normalized = this.normalizeBrDateInput(rawValue);
    this.taskDetailStartDateDisplay = normalized;
    this.taskDetailStartDateInvalid = false;

    if (!normalized) {
      this.taskDetailForm.patchValue({ start_date: '' }, { emitEvent: false });
      this.taskDetailStartDatePickerValue = null;
      return;
    }

    if (normalized.length < 10) {
      this.taskDetailForm.patchValue({ start_date: '' }, { emitEvent: false });
      this.taskDetailStartDatePickerValue = null;
      return;
    }

    const parsed = this.parseBrDate(normalized);
    if (!parsed) {
      this.taskDetailForm.patchValue({ start_date: '' }, { emitEvent: false });
      this.taskDetailStartDatePickerValue = null;
      this.taskDetailStartDateInvalid = true;
      return;
    }

    this.taskDetailStartDateInvalid = false;
    this.taskDetailStartDatePickerValue = parsed;
    this.taskDetailForm.patchValue({ start_date: this.toIsoDate(parsed) }, { emitEvent: false });
  }

  onTaskDetailStartDateBlur() {
    if (!this.taskDetailStartDateDisplay) {
      this.taskDetailStartDateInvalid = false;
      this.taskDetailForm.patchValue({ start_date: '' }, { emitEvent: false });
      this.taskDetailStartDatePickerValue = null;
      return;
    }
    const parsed = this.parseBrDate(this.taskDetailStartDateDisplay);
    if (!parsed) {
      this.taskDetailStartDateInvalid = true;
      this.taskDetailForm.patchValue({ start_date: '' }, { emitEvent: false });
      this.taskDetailStartDatePickerValue = null;
      return;
    }
    this.taskDetailStartDateInvalid = false;
    this.taskDetailStartDateDisplay = this.formatBrDate(parsed);
    this.taskDetailStartDatePickerValue = parsed;
    this.taskDetailForm.patchValue({ start_date: this.toIsoDate(parsed) }, { emitEvent: false });
  }

  onTaskDetailStartDatePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked) {
      return;
    }
    const normalized = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate());
    this.taskDetailStartDateInvalid = false;
    this.taskDetailStartDatePickerValue = normalized;
    this.taskDetailStartDateDisplay = this.formatBrDate(normalized);
    this.taskDetailForm.patchValue({ start_date: this.toIsoDate(normalized) }, { emitEvent: false });
  }

  onTaskDetailDueDateInput(rawValue: string) {
    const normalized = this.normalizeBrDateInput(rawValue);
    this.taskDetailDueDateDisplay = normalized;
    this.taskDetailDueDateInvalid = false;

    if (!normalized) {
      this.taskDetailForm.patchValue({ due_date: '' }, { emitEvent: false });
      this.taskDetailDueDatePickerValue = null;
      return;
    }

    if (normalized.length < 10) {
      this.taskDetailForm.patchValue({ due_date: '' }, { emitEvent: false });
      this.taskDetailDueDatePickerValue = null;
      return;
    }

    const parsed = this.parseBrDate(normalized);
    if (!parsed) {
      this.taskDetailForm.patchValue({ due_date: '' }, { emitEvent: false });
      this.taskDetailDueDatePickerValue = null;
      this.taskDetailDueDateInvalid = true;
      return;
    }

    this.taskDetailDueDateInvalid = false;
    this.taskDetailDueDatePickerValue = parsed;
    this.taskDetailForm.patchValue({ due_date: this.toIsoDate(parsed) }, { emitEvent: false });
  }

  onTaskDetailDueDateBlur() {
    if (!this.taskDetailDueDateDisplay) {
      this.taskDetailDueDateInvalid = false;
      this.taskDetailForm.patchValue({ due_date: '' }, { emitEvent: false });
      this.taskDetailDueDatePickerValue = null;
      return;
    }
    const parsed = this.parseBrDate(this.taskDetailDueDateDisplay);
    if (!parsed) {
      this.taskDetailDueDateInvalid = true;
      this.taskDetailForm.patchValue({ due_date: '' }, { emitEvent: false });
      this.taskDetailDueDatePickerValue = null;
      return;
    }
    this.taskDetailDueDateInvalid = false;
    this.taskDetailDueDateDisplay = this.formatBrDate(parsed);
    this.taskDetailDueDatePickerValue = parsed;
    this.taskDetailForm.patchValue({ due_date: this.toIsoDate(parsed) }, { emitEvent: false });
  }

  onTaskDetailDueDatePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked) {
      return;
    }
    const normalized = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate());
    this.taskDetailDueDateInvalid = false;
    this.taskDetailDueDatePickerValue = normalized;
    this.taskDetailDueDateDisplay = this.formatBrDate(normalized);
    this.taskDetailForm.patchValue({ due_date: this.toIsoDate(normalized) }, { emitEvent: false });
  }

  addDetailSubtask() {
    if (!this.activeTask || !this.detailSubtaskText.trim()) {
      return;
    }
    const orderIndex = (this.activeTask.subtasks || []).length;
    this.controltaskService.createSubtask(this.activeTask.id, { title: this.detailSubtaskText.trim(), order_index: orderIndex }).subscribe({
      next: (response: any) => {
        this.activeTask!.subtasks = this.activeTask!.subtasks || [];
        this.activeTask!.subtasks.push({
          id: response.id,
          title: this.detailSubtaskText.trim(),
          completed: 0,
          order_index: orderIndex
        });
        this.detailSubtaskText = '';
      }
    });
  }

  toggleDetailSubtask(subtask: any) {
    if (!this.activeTask) {
      return;
    }
    this.toggleSubtask(this.activeTask, subtask);
  }

  updateSubtaskDueDate(subtask: any, event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input || !this.activeTask) {
      return;
    }
    const value = input.value || null;
    const subtaskId = this.resolveSubtaskId(subtask);
    if (!subtaskId) {
      return;
    }
    this.controltaskService.updateSubtask(subtaskId, { due_date: value }).subscribe({
      next: () => {
        subtask.due_date = value;
      }
    });
  }

  reorderDetailSubtasks(event: CdkDragDrop<any[]>) {
    if (!this.activeTask?.subtasks?.length) {
      return;
    }
    moveItemInArray(this.activeTask.subtasks, event.previousIndex, event.currentIndex);
    this.activeTask.subtasks.forEach((subtask, index) => {
      subtask.order_index = index;
    });
    const updates = this.activeTask.subtasks
      .map((subtask) => {
        const subtaskId = this.resolveSubtaskId(subtask);
        if (!subtaskId) {
          return null;
        }
        return this.controltaskService.updateSubtask(subtaskId, { order_index: subtask.order_index });
      })
      .filter(Boolean);
    if (updates.length) {
      forkJoin(updates as any).subscribe({ error: () => {} });
    }
  }

  startEditSubtask(subtask: any) {
    this.editingSubtaskId = subtask.id;
    this.editingSubtaskText = subtask.title || '';
  }

  cancelEditSubtask() {
    this.editingSubtaskId = null;
    this.editingSubtaskText = '';
  }

  saveEditSubtask(subtask: any) {
    if (!this.editingSubtaskText.trim()) {
      return;
    }
    const subtaskId = this.resolveSubtaskId(subtask);
    if (!subtaskId) {
      return;
    }
    this.controltaskService.updateSubtask(subtaskId, { title: this.editingSubtaskText.trim() }).subscribe({
      next: () => {
        subtask.title = this.editingSubtaskText.trim();
        this.cancelEditSubtask();
      }
    });
  }

  deleteSubtask(subtask: any) {
    const subtaskId = this.resolveSubtaskId(subtask);
    if (!subtaskId) {
      return;
    }
    this.controltaskService.deleteSubtask(subtaskId).subscribe({
      next: () => {
        if (!this.activeTask) {
          return;
        }
        this.activeTask.subtasks = (this.activeTask.subtasks || []).filter((item: any) => {
          const itemId = this.resolveSubtaskId(item);
          return itemId !== subtaskId;
        });
      }
    });
  }

  openSubtaskDatePicker(input: HTMLInputElement | null) {
    if (!input) {
      return;
    }
    if (typeof (input as any).showPicker === 'function') {
      (input as any).showPicker();
    } else {
      input.click();
    }
  }

  addDetailComment() {
    if (!this.activeTask || !this.userId) {
      return;
    }
    const text = this.detailCommentText.trim();
    const hasFile = !!this.detailCommentFile;
    if (!text && !hasFile) {
      return;
    }

    let payload: any;
    if (hasFile) {
      const formData = new FormData();
      formData.append('user_id', String(this.userId));
      if (this.tenantId) {
        formData.append('tenant_id', String(this.tenantId));
      }
      formData.append('comment', text);
      formData.append('attachment', this.detailCommentFile as File);
      payload = formData;
    } else {
      payload = {
        user_id: this.userId,
        tenant_id: this.tenantId,
        comment: text
      };
    }

    this.controltaskService.addComment(this.activeTask.id, payload).subscribe({
      next: (response: any) => {
        const formatted = {
          id: response.id,
          text: response.comment || response.text,
          author: response.author_name || response.author || 'Você',
          created_at: response.created_at,
          attachments: response.attachments || []
        };
        this.activeTask!.comments = this.activeTask!.comments || [];
        this.activeTask!.comments.unshift(formatted);
        this.detailCommentText = '';
        this.detailCommentFile = null;
        if (this.commentFileInputElement) {
          this.commentFileInputElement.value = '';
        }
      }
    });
  }

  startEditComment(comment: any) {
    this.editingCommentId = comment.id;
    this.editingCommentText = comment.comment || comment.text || '';
  }

  cancelEditComment() {
    this.editingCommentId = null;
    this.editingCommentText = '';
  }

  saveEditComment(comment: any) {
    if (!this.tenantId || !this.editingCommentText.trim()) {
      return;
    }
    this.controltaskService.updateComment(comment.id, { tenant_id: this.tenantId, comment: this.editingCommentText.trim() }).subscribe({
      next: () => {
        comment.comment = this.editingCommentText.trim();
        comment.text = this.editingCommentText.trim();
        this.cancelEditComment();
      }
    });
  }

  deleteComment(comment: any) {
    if (!this.tenantId) {
      return;
    }
    this.controltaskService.deleteComment(this.tenantId, comment.id).subscribe({
      next: () => {
        if (!this.activeTask) {
          return;
        }
        this.activeTask.comments = (this.activeTask.comments || []).filter((item: any) => item.id !== comment.id);
      }
    });
  }

  onCommentFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    this.commentFileInputElement = input;
    const file = input?.files?.[0] || null;
    this.detailCommentFile = file;
  }

  removeCommentFile() {
    this.detailCommentFile = null;
    if (this.commentFileInputElement) {
      this.commentFileInputElement.value = '';
    }
  }

  formatCommentFile(path?: string | null) {
    if (!path) {
      return '';
    }
    if (path.startsWith('http')) {
      return path;
    }
    return `${this.fileHostUrl}${path}`;
  }

  taskAttachmentUrl(file?: { file_path?: string | null }) {
    const path = file?.file_path;
    if (!path) {
      return '#';
    }
    if (path.startsWith('http')) {
      return path;
    }
    return `${this.fileHostUrl}${path}`;
  }

  toInputDate(value: string) {
    const date = new Date(value);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private generateTaskCode() {
    const seed = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += seed[Math.floor(Math.random() * seed.length)];
    }
    return result;
  }

  completedSubtasks(task: ControlTask) {
    if (!task.subtasks?.length) {
      return 0;
    }
    return task.subtasks.filter((item) => !!item.completed).length;
  }

  subtaskProgress(task: ControlTask) {
    if (!task.subtasks?.length) {
      return 0;
    }
    const completed = task.subtasks.filter((item) => !!item.completed).length;
    return Math.round((completed / task.subtasks.length) * 100);
  }

  private toggleSubtask(task: ControlTask, subtask: any) {
    const completed = !subtask.completed;
    const payload: any = { completed };
    if (!completed) {
      payload.completed_at = null;
    }
    const subtaskId = this.resolveSubtaskId(subtask);
    if (!subtaskId) {
      return;
    }
    this.controltaskService.updateSubtask(subtaskId, payload).subscribe({
      next: () => {
        subtask.completed = completed;
        subtask.completed_at = completed ? toLocalISOString(new Date()) : null;
      }
    });
  }

  private resolveSubtaskId(subtask: any): number | null {
    const candidate = subtask?.id ?? subtask?.subtask_id ?? subtask?.subtaskId;
    const parsed = Number(candidate);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  openTicketModal() {
    this.openNewTicket();
  }

  closeTicketModal() {
    this.closeNewTicket();
  }

  submitTicket() {
    this.submitNewTicket();
  }

  openNewTicket() {
    if (!this.newTicketRequestUuid) {
      this.newTicketRequestUuid = this.generateTicketRequestUuid();
    }
    if (this.userDepartmentId) {
      this.newTicketForm.department_id = String(this.userDepartmentId);
    }
    if (!this.newTicketForm.origin_department_id && this.userDepartmentId) {
      this.newTicketForm.origin_department_id = String(this.userDepartmentId);
    }
    this.showNewTicketModal = true;
  }

  closeNewTicket() {
    this.showNewTicketModal = false;
    this.savingNewTicket = false;
    this.resetNewTicketForm();
    this.newTicketFiles = [];
    this.draggingNewTicket = false;
    this.newTicketRequestUuid = '';
  }

  openTicketDetail(ticket: ControlHelpTicket) {
    this.activeTicket = ticket;
    this.ticketDetailTab = 'details';
    this.showTicketDetail = true;
    this.ticketWatchers = [];
    if (this.tenantId) {
      this.fetchTicketComments(ticket.id);
      this.fetchTicketAttachments(ticket.id);
      this.fetchTicketHistory(ticket.id);
    }
  }

  closeTicketDetail() {
    this.showTicketDetail = false;
    this.activeTicket = null;
    this.ticketDetailTab = 'details';
    this.draggingTicketDetailAttachment = false;
  }

  canDeleteTicket(ticket?: ControlHelpTicket | null) {
    if (!ticket) {
      return false;
    }
    if (!this.activeProject || !this.userId) {
      return false;
    }
    const isPrivilegedProfile =
      this.userRoleName.includes('master') ||
      this.userRoleName.includes('admin') ||
      Number(this.userId || 0) === 1;
    const isProjectManager = Number(this.activeProject.manager_user_id || 0) === Number(this.userId);
    const sameDepartment = !!this.userDepartmentId && Number(ticket.department_id || 0) === Number(this.userDepartmentId);
    return isPrivilegedProfile || isProjectManager || sameDepartment;
  }

  deleteTicket(ticket: ControlHelpTicket, event?: Event) {
    event?.stopPropagation();
    if (!this.tenantId || !this.userId || !ticket?.id) {
      return;
    }
    if (!confirm(`Excluir o chamado "${ticket.title}"? Essa ação não pode ser desfeita.`)) {
      return;
    }
    this.deletingTicketId = ticket.id;
    this.controlhelpService.deleteTicket(ticket.id, {
      tenant_id: this.tenantId,
      user_id: this.userId,
      project_id: this.activeProject?.id || null
    }).subscribe({
      next: () => {
        this.tickets = this.tickets.filter((item) => Number(item.id) !== Number(ticket.id));
        if (this.activeTicket && Number(this.activeTicket.id) === Number(ticket.id)) {
          this.closeTicketDetail();
        }
        this.deletingTicketId = null;
      },
      error: () => {
        this.deletingTicketId = null;
      }
    });
  }

  submitNewTicket() {
    if (this.savingNewTicket) {
      return;
    }
    if (!this.newTicketForm.title || !this.newTicketForm.description) {
      return;
    }
    if (!this.tenantId || !this.userId || !this.activeProject) {
      return;
    }
    if (!this.newTicketForm.origin_department_id) {
      return;
    }
    if (!this.newTicketForm.department_id) {
      return;
    }
    this.savingNewTicket = true;
    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      title: this.newTicketForm.title,
      description: this.newTicketForm.description,
      priority: this.newTicketForm.priority,
      category: this.newTicketForm.category || null,
      subcategory: this.newTicketForm.subcategory || null,
      department_id: Number(this.newTicketForm.department_id),
      origin_department_id: this.newTicketForm.origin_department_id
        ? Number(this.newTicketForm.origin_department_id)
        : null,
      project_id: this.activeProject.id,
      request_uuid: this.newTicketRequestUuid
    };
    this.controlhelpService.createTicket(payload).subscribe({
      next: (created) => {
        this.tickets = [created, ...this.tickets];
        if (this.newTicketFiles.length) {
          this.uploadNewTicketAttachments(created.id);
        }
        this.closeNewTicket();
      },
      error: () => {
        this.savingNewTicket = false;
      }
    });
  }

  buildCalendar() {
    const start = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth(), 1);
    const end = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + 1, 0);
    const startWeekDay = start.getDay();
    const days: Array<{ date: Date; label: number; isCurrentMonth: boolean }> = [];

    for (let i = 0; i < startWeekDay; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() - (startWeekDay - i));
      days.push({ date, label: date.getDate(), isCurrentMonth: false });
    }

    for (let i = 1; i <= end.getDate(); i++) {
      const date = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth(), i);
      days.push({ date, label: i, isCurrentMonth: true });
    }

    while (days.length % 7 !== 0) {
      const date = new Date(end);
      date.setDate(date.getDate() + (days.length % 7));
      days.push({ date, label: date.getDate(), isCurrentMonth: false });
    }

    this.calendarDays = days;
  }

  nextMonth() {
    this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + 1, 1);
    this.buildCalendar();
  }

  prevMonth() {
    this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() - 1, 1);
    this.buildCalendar();
  }

  milestoneCount(date: Date) {
    return this.milestones.filter((m) => {
      const due = new Date(m.due_date);
      return (
        due.getFullYear() === date.getFullYear() &&
        due.getMonth() === date.getMonth() &&
        due.getDate() === date.getDate()
      );
    }).length;
  }

  taskCount(date: Date) {
    return this.tasksForDate(date).length;
  }

  private normalizeDate(value: Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private taskDateRange(task: ControlTask) {
    const rawStart = task.start_date ? new Date(task.start_date) : null;
    const rawEnd = task.due_date ? new Date(task.due_date) : null;
    const start = rawStart ? this.normalizeDate(rawStart) : null;
    const end = rawEnd ? this.normalizeDate(rawEnd) : null;
    if (start && end && end < start) {
      return { start: end, end: start };
    }
    if (start && !end) {
      return { start, end: start };
    }
    if (!start && end) {
      return { start: end, end };
    }
    return { start, end };
  }

  tasksForDate(date: Date) {
    const target = this.normalizeDate(date);
    return this.tasks.filter((task) => {
      const range = this.taskDateRange(task);
      if (!range.start || !range.end) {
        return false;
      }
      return target >= range.start && target <= range.end;
    });
  }

  isTaskStart(date: Date, task: ControlTask) {
    const range = this.taskDateRange(task);
    if (!range.start) {
      return false;
    }
    const target = this.normalizeDate(date);
    return target.getTime() === range.start.getTime();
  }

  isTaskEnd(date: Date, task: ControlTask) {
    const range = this.taskDateRange(task);
    if (!range.end) {
      return false;
    }
    const target = this.normalizeDate(date);
    return target.getTime() === range.end.getTime();
  }

  isTaskSingleDay(task: ControlTask) {
    const range = this.taskDateRange(task);
    if (!range.start || !range.end) {
      return false;
    }
    return range.start.getTime() === range.end.getTime();
  }

  isProjectRange(date: Date) {
    if (!this.activeProject?.start_date || !this.activeProject?.end_date) {
      return false;
    }
    const start = new Date(this.activeProject.start_date);
    const end = new Date(this.activeProject.end_date);
    return date >= start && date <= end;
  }

  formatDateInput(value?: string | null) {
    if (!value) {
      return '';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return toLocalDateTimeInputValue(parsed);
  }

  formatHistoryDetails(item: any) {
    if (!item?.details) {
      return '';
    }
    try {
      const parsed = JSON.parse(item.details);
      if (item.action_type === 'DATE_CHANGED') {
        const fromStart = parsed?.from?.start_date ? new Date(parsed.from.start_date).toLocaleDateString('pt-BR') : '—';
        const fromEnd = parsed?.from?.end_date ? new Date(parsed.from.end_date).toLocaleDateString('pt-BR') : '—';
        const toStart = parsed?.to?.start_date ? new Date(parsed.to.start_date).toLocaleDateString('pt-BR') : '—';
        const toEnd = parsed?.to?.end_date ? new Date(parsed.to.end_date).toLocaleDateString('pt-BR') : '—';
        const reason = parsed?.reason ? `Justificativa: ${parsed.reason}` : '';
        return `De ${fromStart}–${fromEnd} para ${toStart}–${toEnd}. ${reason}`.trim();
      }
      return item.details;
    } catch {
      return item.details;
    }
  }

  historyActionLabel(actionType?: string) {
    const map: Record<string, string> = {
      DATE_CHANGED: 'Alteração de data',
      CREATED: 'Criação',
      UPDATED: 'Atualização',
      DELETED: 'Exclusão',
      STATUS_CHANGED: 'Alteração de status'
    };
    if (!actionType) {
      return '—';
    }
    return map[actionType] || actionType.replace(/_/g, ' ').toLowerCase();
  }

  private refreshHistoryTimeline() {
    this.historyTimeline = this.buildHistoryTimeline();
    this.syncHistorySidebarPagination();
  }

  private syncHistorySidebarPagination() {
    const totalPages = this.historySidebarTotalPages;
    if (this.historySidebarCurrentPage > totalPages) {
      this.historySidebarCurrentPage = totalPages;
    }
    if (this.historySidebarCurrentPage < 1) {
      this.historySidebarCurrentPage = 1;
    }
  }

  private buildHistoryTimeline(): ProjectHistoryItem[] {
    const timeline: ProjectHistoryItem[] = [];

    (this.history || []).forEach((item) => {
      timeline.push({
        type: 'PROJECT',
        title: this.historyActionLabel(item.action_type),
        date: item.created_at,
        userName: item.created_by_name || 'Sistema',
        details: this.formatHistoryDetails(item)
      });
    });

    (this.milestones || []).forEach((milestone) => {
      timeline.push({
        type: 'MILESTONE',
        title: `Marco: ${milestone.title}`,
        date: milestone.created_at || milestone.due_date,
        userName: this.userNameById(milestone.created_by),
        details: milestone.due_date ? `Data prevista: ${this.formatDateLabel(milestone.due_date)}` : null,
        description: milestone.description || null
      });
    });

    (this.tasks || []).forEach((task) => {
      const dateValue = task.updated_at || task.created_at;
      timeline.push({
        type: 'TASK',
        title: `Tarefa atualizada: ${task.title}`,
        date: dateValue,
        userName: task.creator_name || this.userNameById(task.created_by),
        details: task.status ? `Status: ${task.status}` : null
      });
    });

    (this.tickets || []).forEach((ticket) => {
      const dateValue = ticket.last_action_at || ticket.opened_at;
      timeline.push({
        type: 'TICKET',
        title: `Chamado atualizado: ${ticket.title || ticket.ticket_code || 'Sem título'}`,
        date: dateValue,
        userName: ticket.created_by_name || this.userNameById(ticket.created_by),
        details: ticket.status ? `Status: ${ticket.status}` : null
      });
    });

    (this.tapVersions || []).forEach((tap) => {
      timeline.push({
        type: 'TAP',
        title: `TAP atualizado (v${tap.version_number})`,
        date: tap.created_at,
        userName: this.userNameById(tap.created_by),
        details: tap.title ? `Título: ${tap.title}` : null
      });
    });

    return timeline.sort((a, b) => this.historySortValue(b.date) - this.historySortValue(a.date));
  }

  private historySortValue(dateValue?: string | null) {
    if (!dateValue) {
      return 0;
    }
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) {
      return 0;
    }
    return parsed.getTime();
  }

  private formatDateLabel(dateValue?: string | null) {
    if (!dateValue) {
      return '—';
    }
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleDateString('pt-BR');
  }

  historyTypeLabel(type?: HistoryItemType) {
    const map: Record<HistoryItemType, string> = {
      PROJECT: 'Projeto',
      MILESTONE: 'Marco',
      TASK: 'Tarefa',
      TICKET: 'Chamado',
      TAP: 'TAP'
    };
    if (!type) {
      return 'Registro';
    }
    return map[type] || 'Registro';
  }

  loadProjects() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.controlprojectService.getProjects(this.tenantId, {
      user_id: this.userId,
      search: this.searchTerm
    }).subscribe({
      next: (rows) => {
        this.projects = rows || [];
        this.projectCurrentPage = 1;
        this.buildProjectDashboardInsights();
      },
      error: () => {
        this.projects = [];
        this.projectCurrentPage = 1;
        this.buildProjectDashboardInsights();
        this.clearPersistedActiveProjectId();
      }
    });
  }

  private restoreActiveProjectFromStorage() {
    return;
  }

  private buildStatusFiltersStorageKey() {
    const tenantPart = this.tenantId || 'none';
    const userPart = this.userId || 'none';
    return `${this.statusFiltersStorageKey}:${tenantPart}:${userPart}`;
  }

  private restorePersistedStatusFilters() {
    try {
      const raw = localStorage.getItem(this.buildStatusFiltersStorageKey());
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as { pinned?: boolean; statusFilters?: ProjectStatusFilter[] } | null;
      if (!parsed || !parsed.pinned || !Array.isArray(parsed.statusFilters)) {
        return;
      }
      this.statusFiltersPinned = true;
      this.statusFilters = this.normalizeStatusFilters(parsed.statusFilters);
    } catch {
      // ignore storage issues
    }
  }

  private persistStatusFiltersPreference() {
    if (!this.statusFiltersPinned) {
      return;
    }
    try {
      localStorage.setItem(
        this.buildStatusFiltersStorageKey(),
        JSON.stringify({
          pinned: true,
          statusFilters: this.normalizeStatusFilters(this.statusFilters)
        })
      );
    } catch {
      // ignore storage issues
    }
  }

  private clearPersistedStatusFilters() {
    try {
      localStorage.removeItem(this.buildStatusFiltersStorageKey());
    } catch {
      // ignore storage issues
    }
  }

  private normalizeStatusFilters(filters: ProjectStatusFilter[]): ProjectStatusFilter[] {
    if (!filters || !filters.length || filters.includes('ALL')) {
      return ['ALL'] as ProjectStatusFilter[];
    }
    return filters.filter((value): value is ProjectStatusFilter => value !== 'ALL');
  }

  private buildActiveProjectStorageKey() {
    const tenantPart = this.tenantId || 'none';
    const userPart = this.userId || 'none';
    return `${this.activeProjectStorageKey}:${tenantPart}:${userPart}`;
  }

  private persistActiveProjectId(projectId: number) {
    try {
      localStorage.setItem(this.buildActiveProjectStorageKey(), String(projectId));
    } catch {
      // ignore storage issues (private mode, quota, etc.)
    }
  }

  private readPersistedActiveProjectId(): number | null {
    try {
      const raw = localStorage.getItem(this.buildActiveProjectStorageKey());
      const parsed = Number(raw);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    } catch {
      return null;
    }
  }

  private clearPersistedActiveProjectId() {
    try {
      localStorage.removeItem(this.buildActiveProjectStorageKey());
    } catch {
      // ignore storage issues
    }
  }

  private loadProjectDetail(projectId: number) {
    if (!this.tenantId) {
      return;
    }
    this.historySidebarSearchTerm = '';
    this.historySidebarCurrentPage = 1;
    this.historyTimeline = [];
    this.history = [];
    this.milestones = [];
    this.tasks = [];
    this.visibleProjectTasks = [];
    this.tickets = [];
    this.tapVersions = [];
    this.projectAttachments = [];
    this.controlprojectService.getProject(this.tenantId, projectId).subscribe({
      next: (project) => (this.activeProject = project)
    });
    this.controlprojectService.getMembers(this.tenantId, projectId).subscribe({
      next: (rows) => {
        this.projectMembers = rows || [];
        this.refreshProjectAssigneeOptions();
      },
      error: () => {
        this.projectMembers = [];
        this.refreshProjectAssigneeOptions();
      }
    });
    this.controlprojectService.getTapVersions(this.tenantId, projectId).subscribe({
      next: (rows) => {
        this.tapVersions = rows || [];
        this.refreshHistoryTimeline();
      },
      error: () => {
        this.tapVersions = [];
        this.refreshHistoryTimeline();
      }
    });
    this.controlprojectService.getMilestones(this.tenantId, projectId).subscribe({
      next: (rows) => {
        this.milestones = rows || [];
        this.refreshHistoryTimeline();
      },
      error: () => {
        this.milestones = [];
        this.refreshHistoryTimeline();
      }
    });
    this.controltaskService.getTasks(this.tenantId, { project_id: projectId }).subscribe({
      next: (rows) => {
        this.tasks = rows || [];
        this.refreshVisibleProjectTasks();
        this.refreshHistoryTimeline();
      },
      error: () => {
        this.tasks = [];
        this.visibleProjectTasks = [];
        this.refreshHistoryTimeline();
      }
    });
    this.controlhelpService.getTickets(this.tenantId, { project_id: projectId }).subscribe({
      next: (rows) => {
        this.tickets = rows || [];
        this.refreshHistoryTimeline();
      },
      error: () => {
        this.tickets = [];
        this.refreshHistoryTimeline();
      }
    });
    this.loadProjectAttachments(projectId);
    this.loadProjectHistory(projectId);
  }

  private loadProjectHistory(projectId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlprojectService.getHistory(this.tenantId, projectId).subscribe({
      next: (rows) => {
        this.history = rows || [];
        this.refreshHistoryTimeline();
      },
      error: () => {
        this.history = [];
        this.refreshHistoryTimeline();
      }
    });
  }

  private fetchUsers() {
    if (!this.tenantId) {
      return;
    }
    this.userService.getUsersByTenant(this.tenantId).subscribe({
      next: (rows) => {
        this.users = rows || [];
        this.refreshProjectAssigneeOptions();
        this.refreshHistoryTimeline();
      },
      error: () => {
        this.users = [];
        this.refreshProjectAssigneeOptions();
      }
    });
  }

  private fetchDepartments() {
    if (!this.tenantId) {
      return;
    }
    this.orgStructureService.getDepartments(this.tenantId).subscribe({
      next: (rows) => {
        this.departments = (rows || []).slice().sort((a, b) => {
          const labelA = a.sector_name ? `${a.sector_name} / ${a.name}` : a.name;
          const labelB = b.sector_name ? `${b.sector_name} / ${b.name}` : b.name;
          return String(labelA).localeCompare(String(labelB));
        });
        if (!this.userSectorId && this.userDepartmentId) {
          const dept = this.departments.find((d) => Number(d.id) === Number(this.userDepartmentId));
          this.userSectorId = (dept?.sector_id ?? null) as number | null;
        }
      },
      error: () => (this.departments = [])
    });
  }

  private fetchTicketCategories() {
    if (!this.tenantId) {
      return;
    }
    // Carrega todas as categorias para permitir seleção por departamento no chamado.
    this.controlhelpService.getCategories(this.tenantId).subscribe({
      next: (rows) => (this.ticketCategories = rows || []),
      error: () => (this.ticketCategories = [])
    });
  }

  private loadGroups() {
    if (!this.tenantId) {
      return;
    }
    this.controltaskService.getGroups(this.tenantId).subscribe({
      next: (groups) => (this.groups = groups || []),
      error: () => (this.groups = [])
    });
  }

  private loadTags() {
    if (!this.tenantId) {
      return;
    }
    this.controltaskService.getTags(this.tenantId).subscribe({
      next: (tags) => (this.tags = tags || []),
      error: () => (this.tags = [])
    });
  }

  private loadSubgroups() {
    if (!this.tenantId) {
      return;
    }
    this.controltaskService.getSubgroups(this.tenantId).subscribe({
      next: (rows) => {
        this.subgroups = rows || [];
        this.refreshDetailFormSubgroups();
      },
      error: () => {
        this.subgroups = [];
        this.refreshDetailFormSubgroups();
      }
    });
  }

  private loadBuckets() {
    if (!this.tenantId) {
      return;
    }
    this.controltaskService.getBuckets(this.tenantId).subscribe({
      next: (rows) => {
        this.buckets = rows || [];
        this.refreshDetailFormBuckets();
      },
      error: () => {
        this.buckets = [];
        this.refreshDetailFormBuckets();
      }
    });
  }

  private loadMembers() {
    if (!this.tenantId) {
      return;
    }
    this.controltaskService.getUsers(this.tenantId).subscribe({
      next: (rows) => (this.members = rows || []),
      error: () => (this.members = [])
    });
  }

  addComment() {
    if (!this.newCommentForm.comment.trim() || !this.activeTicket) {
      return;
    }
    if (!this.tenantId || !this.userId) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      user_id: this.userId,
      comment: this.newCommentForm.comment.trim(),
      is_internal: this.newCommentForm.is_internal
    };
    this.controlhelpService.addComment(this.activeTicket.id, payload).subscribe({
      next: (created) => {
        this.newCommentForm = { comment: '', is_internal: this.newCommentForm.is_internal };
        if (created?.id && this.commentFile) {
          const formData = new FormData();
          formData.append('tenant_id', String(this.tenantId));
          formData.append('uploaded_by', String(this.userId));
          formData.append('comment_id', String(created.id));
          formData.append('file', this.commentFile);
          this.controlhelpService.uploadAttachment(this.activeTicket!.id, formData).subscribe({
            next: () => {
              this.fetchTicketAttachments(this.activeTicket!.id);
              this.fetchTicketHistory(this.activeTicket!.id);
            }
          });
        }
        this.commentFile = null;
        this.fetchTicketComments(this.activeTicket!.id);
        this.fetchTicketHistory(this.activeTicket!.id);
      }
    });
  }

  addAttachment(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []);
    if (!files.length) {
      return;
    }
    if (!this.tenantId || !this.userId || !this.activeTicket) {
      return;
    }
    this.uploadTicketDetailAttachments(files);
    if (input) {
      input.value = '';
    }
  }

  onTicketDetailAttachmentDragOver(event: DragEvent) {
    event.preventDefault();
    this.draggingTicketDetailAttachment = true;
  }

  onTicketDetailAttachmentDragLeave() {
    this.draggingTicketDetailAttachment = false;
  }

  onTicketDetailAttachmentDrop(event: DragEvent) {
    event.preventDefault();
    this.draggingTicketDetailAttachment = false;
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) {
      return;
    }
    this.uploadTicketDetailAttachments(files);
  }

  onTicketCommentFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] || null;
    this.commentFile = file;
    if (input) {
      input.value = '';
    }
  }

  onNewTicketDragOver(event: DragEvent) {
    event.preventDefault();
    this.draggingNewTicket = true;
  }

  onNewTicketDragLeave() {
    this.draggingNewTicket = false;
  }

  onNewTicketDrop(event: DragEvent) {
    event.preventDefault();
    this.draggingNewTicket = false;
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length) {
      this.newTicketFiles = [...this.newTicketFiles, ...files];
    }
  }

  onNewTicketFileSelect(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []);
    if (files.length) {
      this.newTicketFiles = [...this.newTicketFiles, ...files];
    }
    if (input) {
      input.value = '';
    }
  }

  removeNewTicketFile(index: number) {
    this.newTicketFiles = this.newTicketFiles.filter((_item, idx) => idx !== index);
  }

  assignTicket(user: string) {
    if (!this.activeTicket) {
      return;
    }
    if (!this.tenantId || !this.userId) {
      return;
    }
    const assignedId = user ? Number(user) : null;
    this.controlhelpService
      .updateTicket(this.activeTicket.id, { tenant_id: this.tenantId, user_id: this.userId, assigned_to: assignedId })
      .subscribe({
        next: (updated) => {
          this.activeTicket = updated;
          this.tickets = this.tickets.map((t) => (t.id === updated.id ? updated : t));
          this.fetchTicketHistory(updated.id);
        }
      });
  }

  changeStatus(status: ControlHelpStatus) {
    if (!this.activeTicket) {
      return;
    }
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.controlhelpService
      .updateTicket(this.activeTicket.id, { tenant_id: this.tenantId, user_id: this.userId, status })
      .subscribe({
        next: (updated) => {
          this.activeTicket = updated;
          this.tickets = this.tickets.map((t) => (t.id === updated.id ? updated : t));
          this.fetchTicketHistory(updated.id);
        }
      });
  }

  changePriority(priority: ControlHelpPriority) {
    if (!this.activeTicket) {
      return;
    }
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.controlhelpService
      .updateTicket(this.activeTicket.id, { tenant_id: this.tenantId, user_id: this.userId, priority })
      .subscribe({
        next: (updated) => {
          this.activeTicket = updated;
          this.tickets = this.tickets.map((t) => (t.id === updated.id ? updated : t));
          this.fetchTicketHistory(updated.id);
        }
      });
  }

  changeDueAt(value: string) {
    if (!this.activeTicket || !this.tenantId || !this.userId) {
      return;
    }
    let normalized: string | null = null;
    if (value) {
      const parsed = new Date(value);
      normalized = Number.isNaN(parsed.getTime()) ? null : toLocalSQLDateTime(parsed);
    }
    this.controlhelpService
      .updateTicket(this.activeTicket.id, { tenant_id: this.tenantId, user_id: this.userId, due_at: normalized })
      .subscribe({
        next: (updated) => {
          this.activeTicket = updated;
          this.tickets = this.tickets.map((t) => (t.id === updated.id ? updated : t));
          this.fetchTicketHistory(updated.id);
        }
      });
  }

  addWatcher(userId: string) {
    const id = Number(userId);
    if (!id) {
      return;
    }
    const name = this.users.find((u) => u.id === id)?.name;
    if (!name || this.ticketWatchers.includes(name)) {
      return;
    }
    this.ticketWatchers = [...this.ticketWatchers, name];
  }

  removeWatcher(user: string) {
    this.ticketWatchers = this.ticketWatchers.filter((w) => w !== user);
  }

  ticketViewLabel() {
    return 'Chamados do projeto';
  }

  ticketStatusLabel(status?: string | null) {
    const map: Record<string, string> = {
      OPEN: 'Aberto',
      IN_PROGRESS: 'Em andamento',
      WAITING_USER: 'Aguardando usuário',
      WAITING_THIRD_PARTY: 'Aguardando terceiro',
      RESOLVED: 'Resolvido',
      CLOSED: 'Fechado',
      CANCELED: 'Cancelado'
    };
    if (!status) {
      return 'Sem status';
    }
    return map[status] || status;
  }

  ticketPriorityLabel(priority?: string | null) {
    const map: Record<string, string> = {
      LOW: 'Baixa',
      MEDIUM: 'Média',
      HIGH: 'Alta',
      URGENT: 'Urgente'
    };
    if (!priority) {
      return 'Sem prioridade';
    }
    return map[priority] || priority;
  }

  userNameById(id?: number | null) {
    if (!id) {
      return 'Sistema';
    }
    return this.users.find((u) => u.id === id)?.name || 'Sistema';
  }

  fileUrl(file: ControlHelpAttachment) {
    if (!file.file_path) {
      return '#';
    }
    if (file.file_path.startsWith('http')) {
      return file.file_path;
    }
    return `${API_BASE_URL}${file.file_path}`;
  }

  commentAttachments(commentId: number) {
    return this.ticketAttachments.filter((file) => Number(file.comment_id) === Number(commentId));
  }

  standaloneAttachments() {
    return this.ticketAttachments.filter((file) => !file.comment_id);
  }

  historyLabel(item: ControlHelpHistory) {
    if (item.description) {
      return item.description;
    }
    const map: Record<string, string> = {
      CREATED: 'Chamado aberto',
      COMMENTED: 'Comentário adicionado',
      STATUS_CHANGED: 'Status atualizado',
      PRIORITY_CHANGED: 'Prioridade atualizada',
      ASSIGNED: 'Responsável atualizado',
      ATTACHMENT_ADDED: 'Anexo adicionado',
      DUE_DATE_SET: 'Prazo definido'
    };
    return map[item.action_type] || item.action_type;
  }

  canEditActiveTicket() {
    if (!this.activeTicket || !this.userDepartmentId) {
      return false;
    }
    return Number(this.activeTicket.department_id) === Number(this.userDepartmentId);
  }

  formatDateTimeLocal(value?: string | null) {
    if (!value) {
      return '';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return toLocalDateTimeInputValue(parsed);
  }

  categoriesByDepartment() {
    const id = Number(this.newTicketForm.department_id);
    if (!id) {
      return [];
    }
    const unique = new Map<string, ControlHelpCategory>();
    this.ticketCategories
      .filter((c) => Number(c.department_id) === id)
      .forEach((c) => {
        if (!unique.has(c.name)) {
          unique.set(c.name, c);
        }
      });
    return Array.from(unique.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }

  subcategoriesByDepartment() {
    const id = Number(this.newTicketForm.department_id);
    if (!id || !this.newTicketForm.category) {
      return [];
    }
    return this.ticketCategories
      .filter((c) => Number(c.department_id) === id && c.name === this.newTicketForm.category && c.subcategory)
      .map((c) => c.subcategory as string)
      .sort((a, b) => String(a).localeCompare(String(b)));
  }

  originDepartments() {
    if (this.userSectorId) {
      return this.departments.filter((dept) => Number(dept.sector_id) === Number(this.userSectorId));
    }
    if (this.userDepartmentId) {
      return this.departments.filter((dept) => Number(dept.id) === Number(this.userDepartmentId));
    }
    return this.departments;
  }

  departmentLabel(departmentId?: number | null) {
    const dept = this.departments.find((d) => d.id === Number(departmentId));
    if (!dept) {
      return 'Não informado';
    }
    return dept.sector_name ? `${dept.sector_name} / ${dept.name}` : dept.name;
  }

  private uploadNewTicketAttachments(ticketId: number) {
    if (!this.tenantId || !this.userId || !this.newTicketFiles.length) {
      return;
    }
    const uploads = this.newTicketFiles.map((file) => {
      const formData = new FormData();
      formData.append('tenant_id', String(this.tenantId));
      formData.append('uploaded_by', String(this.userId));
      formData.append('file', file);
      return this.controlhelpService.uploadAttachment(ticketId, formData);
    });
    forkJoin(uploads.length ? uploads : [of(null)]).subscribe(() => {
      this.newTicketFiles = [];
    });
  }

  private uploadTicketDetailAttachments(files: File[]) {
    if (!this.tenantId || !this.userId || !this.activeTicket || !files.length) {
      return;
    }
    const uploads = files.map((file) => {
      const formData = new FormData();
      formData.append('tenant_id', String(this.tenantId));
      formData.append('uploaded_by', String(this.userId));
      formData.append('file', file);
      return this.controlhelpService.uploadAttachment(this.activeTicket!.id, formData);
    });
    forkJoin(uploads.length ? uploads : [of(null)]).subscribe({
      next: () => {
        this.fetchTicketAttachments(this.activeTicket!.id);
        this.fetchTicketHistory(this.activeTicket!.id);
      }
    });
  }

  private fetchTicketComments(ticketId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlhelpService.getComments(this.tenantId, ticketId).subscribe({
      next: (rows) => (this.ticketComments = rows || []),
      error: () => (this.ticketComments = [])
    });
  }

  private fetchTicketAttachments(ticketId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlhelpService.getAttachments(this.tenantId, ticketId).subscribe({
      next: (rows) => (this.ticketAttachments = rows || []),
      error: () => (this.ticketAttachments = [])
    });
  }

  private fetchTicketHistory(ticketId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlhelpService.getHistory(this.tenantId, ticketId).subscribe({
      next: (rows) => (this.ticketHistory = rows || []),
      error: () => (this.ticketHistory = [])
    });
  }

  private resetNewTicketForm() {
    this.newTicketForm = {
      title: '',
      description: '',
      priority: 'MEDIUM',
      category: '',
      subcategory: '',
      department_id: '',
      origin_department_id: this.userDepartmentId ? String(this.userDepartmentId) : ''
    };
  }

  private generateTicketRequestUuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `ticket-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
  }

  onDepartmentChange() {
    this.newTicketForm.category = '';
    this.newTicketForm.subcategory = '';
  }

  private resetProjectForm() {
    this.newProjectForm = {
      name: '',
      description: '',
      status: 'PLANNED',
      start_date: '',
      end_date: '',
      manager_user_id: '',
      department_id: ''
    };
    this.syncProjectDateFields();
    this.resetTapForm();
  }

  private syncProjectDateFields() {
    const startDate = this.parseIsoDate(this.newProjectForm.start_date);
    const endDate = this.parseIsoDate(this.newProjectForm.end_date);

    this.projectStartDatePickerValue = startDate;
    this.projectEndDatePickerValue = endDate;
    this.projectStartDateDisplay = startDate ? this.formatBrDate(startDate) : '';
    this.projectEndDateDisplay = endDate ? this.formatBrDate(endDate) : '';
    this.projectStartDateInvalid = false;
    this.projectEndDateInvalid = false;
  }

  private syncMilestoneDateFields() {
    const dueDate = this.parseIsoDate(this.newMilestoneForm.due_date);
    this.milestoneDueDatePickerValue = dueDate;
    this.milestoneDueDateDisplay = dueDate ? this.formatBrDate(dueDate) : '';
    this.milestoneDueDateInvalid = false;
  }

  private syncDateEditDateFields() {
    const startDate = this.parseIsoDate(this.dateEditForm.start_date);
    const endDate = this.parseIsoDate(this.dateEditForm.end_date);
    this.dateEditStartDatePickerValue = startDate;
    this.dateEditEndDatePickerValue = endDate;
    this.dateEditStartDateDisplay = startDate ? this.formatBrDate(startDate) : '';
    this.dateEditEndDateDisplay = endDate ? this.formatBrDate(endDate) : '';
    this.dateEditStartDateInvalid = false;
    this.dateEditEndDateInvalid = false;
  }

  private syncTaskDetailDateFields() {
    const startDate = this.parseIsoDate(this.taskDetailForm.get('start_date')?.value);
    const dueDate = this.parseIsoDate(this.taskDetailForm.get('due_date')?.value);
    this.taskDetailStartDatePickerValue = startDate;
    this.taskDetailDueDatePickerValue = dueDate;
    this.taskDetailStartDateDisplay = startDate ? this.formatBrDate(startDate) : '';
    this.taskDetailDueDateDisplay = dueDate ? this.formatBrDate(dueDate) : '';
    this.taskDetailStartDateInvalid = false;
    this.taskDetailDueDateInvalid = false;
  }

  private normalizeBrDateInput(value: string) {
    const digits = (value || '').replace(/\D/g, '').slice(0, 8);
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    if (digits.length <= 2) {
      return day;
    }
    if (digits.length <= 4) {
      return `${day}/${month}`;
    }
    return `${day}/${month}/${year}`;
  }

  private parseBrDate(value: string): Date | null {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (!match) {
      return null;
    }
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    if (!day || !month || !year) {
      return null;
    }
    const candidate = new Date(year, month - 1, day);
    if (
      candidate.getFullYear() !== year ||
      candidate.getMonth() !== month - 1 ||
      candidate.getDate() !== day
    ) {
      return null;
    }
    return candidate;
  }

  private parseIsoDate(value?: string | null): Date | null {
    const normalized = (value || '').trim();
    if (!normalized) {
      return null;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const parsed = new Date(year, month - 1, day);
      if (
        parsed.getFullYear() === year &&
        parsed.getMonth() === month - 1 &&
        parsed.getDate() === day
      ) {
        return parsed;
      }
      return null;
    }
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }

  private formatBrDate(value: Date) {
    const day = `${value.getDate()}`.padStart(2, '0');
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const year = `${value.getFullYear()}`;
    return `${day}/${month}/${year}`;
  }

  private toIsoDate(value: Date) {
    const day = `${value.getDate()}`.padStart(2, '0');
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const year = `${value.getFullYear()}`;
    return `${year}-${month}-${day}`;
  }

  private resetTapForm() {
    this.tapForm = {
      title: '',
      justification: '',
      objectives: '',
      business_scope: '',
      technical_scope: '',
      deliverables: '',
      non_deliverables: '',
      permissions: '',
      constraints: '',
      assumptions: '',
      risks: '',
      success_criteria: '',
      budget: ''
    };
  }
}
